<template>
<div class="chapter">
<div class="inner_page">
<div class="chapter" title="Starters">
<div class="titlepage">
<div>
<div>
<h1 class="title"><a id="d0e629"></a>Starters </h1>
</div>
</div>
</div>
<div class="mediaobject" title="Photo of figs.">
<img alt="Photo of figs." src="images/RCM_8290.jpg"/>
</div>
<p>Southern hospitality is more than a cliché. Historically, plantations were far apart
                and transportation was rudimental. Guests, invited or unexpected, were welcomed and
                fed in good times and bad. Hard times, like the Great Depression, were times for
                helping each other. Even in good times, welcoming included offering something to eat
                or drink, to start the occasion, large or small. </p>
<p>In a region that has enjoyed big, mid-day farm lunches (called dinner) and three
                o’clock dinner in Charleston, it is hard to say when starters started, just as it is
                hard to say what a starter is. The word <span class="emphasis"><em>canapé</em></span> is derived from the French word for “couch,” for
                instance. <span class="emphasis"><em>Hors d’oeuvre</em></span> means, according to
                    <span class="emphasis"><em>Food Lover’s Companion,</em></span> “outside the
                work” meal, or food outside the work. <span class="emphasis"><em>Starters</em></span> is a word we have adopted to mean something we can eat
                anytime short of a main course. </p>
<p>Starters have gone from a narrow range of options to a broad swath of foods presented
                in new and exciting ways. The highlights once were when one was offered a ham
                biscuit or cheese straws, doled out stingily to children or offered graciously to
                adults. On a sideboard or buffet table, shrimp was draped around the edges and on
                top of ice in a large punch bowl with a dish of cocktail sauce in the center. </p>
<p>We’ve had celery sticks (which I still love) filled with pimento cheese or peanut
                butter; roasted pecans, peanuts, or variations thereof; various dips, including
                artichoke, crab and onion made from packaged onion soup and sour cream, served with
                chips; oysters wrapped in bacon, hopefully crisp, but often not. </p>
<p>Some of these remain in our repertoire, but we’ve kept them simple and resonant to
                the culture. Along our coast we use fresh crab, finding the canned not worth the
                effort; pimento cheese is still high on every Southerner’s list, and we’ve filled a
                tart with it. Rather than ubiquitous cubes of mediocre cheese, we’ve poured melted
                hot pepper jelly over logs of goat cheese. Fresh vegetables shine, such as zucchini
                rounds with a little bit of grated carrots. </p>
<p>A variety of ingredients have become available to stimulate the imagination. The
                English custom of tea with sandwiches and other delicacies was certainly not lost on
                Southerners, even after we started dumping enough sugar in our tea to make a spoon
                stand up unaided and enough ice to chill an igloo. Tiny grits cakes with shrimp and
                bacon; peaches and figs with country ham; savory tarts and custards that could
                replace soup or salad at the table or be served in the living room have all changed
                the order of meals. </p>
<p>Cynthia’s husband, Cliff, has a habit of fixing little nibbles for the family while
                waiting for dinner. He might show up with a chopped-tomato-and-basil concoction or a
                chicken liver paté he whipped up in the food processor, with a glass of wine rather
                than the still popular bourbon and branch water his neighbor drinks. Many of us
                don’t even know when we are hungry—my French son-in-law, Pierre Henri, who frowns at
                nibbling outside of meals and is no fan of canapés, says that when one eats, the
                hunger comes. So a starter should be only that—something to whet the appetite,
                readying one to sit down and eat a meal. </p>
<h2><a id="d0e663"></a>Peaches and Figs Wrapped in Country Ham </h2>
<p>
<span class="emphasis">
<em>Serves 2 </em>
</span>
</p>
<p>Traditionally in Europe, melons are wrapped in prosciutto ham and served as a starter
                or appetizer. Our thinly sliced country ham is supple enough to wrap equally well
                and is enjoyed by all, whether with peaches, figs, melons, or savory crunchy
                vegetables. Serve as a nibble on toothpicks or plated as a starter. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1 fresh peach </td>
</tr>
<tr>
<td>6 fresh figs, stems removed </td>
</tr>
<tr>
<td>2 to 3 biscuit or thin slices of country ham </td>
</tr>
</tbody></table>
<p>Peel and slice the peach into wedges. Cut an X in the top of each fig, cutting
                three-fourths of the way down, keeping the sections attached at the base. Tear the
                country ham into strips. </p>
<p>Roll a small portion of country ham around a finger and insert into the X on the fig.
                Wrap another ham strip around each peach wedge. Cover with plastic wrap and
                refrigerate until serving time, not more than 2 to 3 hours. </p>
<p><span class="bold"><strong>Variation:</strong></span> Cut peeled cantaloupes or
                mangos in cubes or wedges, and wrap with country ham. Wrap sliced raw or lightly
                cooked asparagus or okra, or picked ramp, okras or other pickles with country ham. </p>
<div class="mediaobject" title="Photo of the recipe or step above.">
<img alt="Photo of the recipe or step above." src="images/_LMP3916.jpg"/>
</div>
<h2><a id="d0e692"></a>Country Ham with Baked Stuffed Figs </h2>
<p>
<span class="emphasis">
<em>Serves 4 </em>
</span>
</p>
<p>The bubbling cheese and lush figs, served on a platter of thinly sliced country ham
                or wrapped in it and served on plates is simple, uses what’s on hand and seasonal,
                and is a combination of comforting and sensual. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>12 figs, stems removed </td>
</tr>
<tr>
<td>6 ounces Clemson Blue Cheese, room temperature </td>
</tr>
<tr>
<td>2 tablespoons finely chopped pecans </td>
</tr>
<tr>
<td>1/2 cup finely chopped fresh herbs, such as thyme, oregano, and/or flat-leaf
                        parsley </td>
</tr>
<tr>
<td>4 ounces thinly sliced country ham </td>
</tr>
</tbody></table>
<p>Preheat oven to 450 degrees. </p>
<p>Cut an X in the top of each fig, cutting three-fourths of the way down, keeping the
                sections attached at the base. Move to an ungreased rimmed baking sheet and gently
                open them. </p>
<p>Mix together the cheese, pecans, and herbs until well blended. Use a small spoon or
                piping bag to gently stuff each fig with filling. Bake the figs for 8 to 10 minutes,
                until the cheese is bubbling. </p>
<p>Arrange the country ham on a platter or individual plates and top with warm figs. It
                is a little tricky, but these can be wrapped in thinly sliced ham and toothpicked if
                necessary.</p>
<div class="sidebar">
<p class="title">
<b></b>
</p>
<p>Clemson Blue Cheese was originally made in an abandoned railway tunnel and is now
                    made in a year-round climate-controlled environment at South Carolina’s Clemson
                    School of Agriculture. It has worked its way into a frequent relationship with
                    fresh figs and country ham. </p>
</div>
<h2><a id="d0e723"></a>Fig and Pecan Tapenade with Goat Cheese </h2>
<p>
<span class="emphasis">
<em>Serves 20 </em>
</span>
</p>
<p>Dried figs keep me going between fresh fig seasons. They pep up dips and spreads.
                Cream cheese has long been the base for many decorative spreads, but soft goat
                cheese has crept up in popularity. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1 cup chopped dried figs </td>
</tr>
<tr>
<td>1/3 cup water </td>
</tr>
<tr>
<td>1/3 cup chopped pitted black olives </td>
</tr>
<tr>
<td>2 tablespoons olive oil </td>
</tr>
<tr>
<td>1 tablespoon red wine vinegar </td>
</tr>
<tr>
<td>1/2 tablespoon chopped green olives, optional </td>
</tr>
<tr>
<td>1-1/2 teaspoons chopped fresh thyme </td>
</tr>
<tr>
<td>1/2 cup chopped toasted pecans</td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>Freshly ground black pepper </td>
</tr>
<tr>
<td>2 (5-1/2-ounce) logs soft goat cheese, cut into 1/2-inch-thick rounds </td>
</tr>
</tbody></table>
<p>Cover figs with water in a heavy saucepan, bring to a simmer, and cook over
                medium-high heat until figs are soft and water has nearly evaporated, about 7
                minutes. </p>
<p>Move figs to a bowl using a slotted spoon and stir in olives, oil, vinegar, green
                olives, thyme, and pecans. Season to taste with salt and pepper. </p>
<p>Arrange goat cheese circles overlapping around a small platter. Spoon fig mixture
                over the cheese. Serve with crackers.</p>
<div class="sidebar">
<p class="title">
<b></b>
</p>
<h4><a id="d0e762" xmlns:saxon="http://icl.com/saxon"></a>Chopping Dried Fruit </h4>
<p>To prevent dried fruit from clinging together while trying to chop it, coat the
                    metal blade lightly with cooking oil or spray. If the dried fruit is to be used
                    in a flour-based recipe, toss the fruit in flour to coat, then chop and return
                    the fruit to the flour. Some cooks have success with chopping dried fruit in the
                    food processor, just pulsing the blade a few times.</p>
</div>
<h2><a id="d0e766" xmlns:saxon="http://icl.com/saxon"></a>Savory Fig Bites </h2>
<p>
<span class="emphasis">
<em>Makes 24 mini tarts </em>
</span>
</p>
<p>Judy Bernstein lives on one of the beautiful islands surrounding Charleston, where
                she serves fresh figs from her trees to make a tarted-up starter. This makes a
                sweet-sour combination medley served in a crispy case. The filling is best made
                ahead to allow the flavors to meld. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>24 mini phyllo cups, store-bought or <span class="link" href="#/ch13#phyllo" title="Link target outside of Sample">homemade</span></td>
</tr>
<tr>
<td>3 to 4 tablespoons butter, melted </td>
</tr>
<tr>
<td>6 ounces soft goat cheese </td>
</tr>
<tr>
<td>1/4 cup heavy cream </td>
</tr>
<tr>
<td>3 tablespoons honey, divided </td>
</tr>
<tr>
<td>Grated rind of 1/2 lemon, no white attached </td>
</tr>
<tr>
<td>1/2 teaspoon chopped fresh rosemary </td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>Freshly ground black pepper </td>
</tr>
<tr>
<td>6 fresh figs, quartered </td>
</tr>
<tr>
<td>1/2 cup sherry vinegar </td>
</tr>
</tbody></table>
<p>Preheat oven to 325 degrees. Lightly butter a mini muffin pan, add the phyllo cups,
                and brush the cups lightly with butter. Bake 5 to 7 minutes, until crisp. Remove and
                set aside. </p>
<p>Mix goat cheese, cream, and 2 tablespoons honey until smooth and creamy. Stir in
                lemon rind and rosemary. Season to taste with salt and pepper. </p>
<p>To assemble, spoon a tablespoon of the goat cheese mixture into a cooled shell. Top
                with a piece of fresh fig. Repeat with remaining shells and sprinkle with black
                pepper. This can be done several hours ahead or up to a day in advance. </p>
<p>Before serving, bring vinegar and remaining 1 tablespoon honey to the boil in a small
                saucepan; simmer until reduced by half and slightly syrupy, 5 to 7 minutes. Drizzle
                over each tart.</p>
<div class="sidebar">
<p class="title">
<b></b>
</p>
<h4><a id="d0e809" xmlns:saxon="http://icl.com/saxon"></a>Another Nosh-able idea for
                    Phyllo Cups </h4>
<p>Using 1-1/4 cups <span class="link" href="#/ch16#pepperjelly" title="Link target outside of Sample">Hot Pepper Jelly</span>, 3 ounces Brie, peeled and cut
                    into 30 small pieces, and 3 tablespoons chopped lightly toasted pecans, fill
                    each phyllo cup with 1/4 teaspoon jelly. Top each with 1 piece of Brie. Sprinkle
                    all with toasted pecans. Bake in a 350-degree oven until cheese melts, about 5
                    to 6 minutes. Serve warm.</p>
</div>
<h2><a id="d0e816" xmlns:saxon="http://icl.com/saxon"></a>Fried Watermelon Rind </h2>
<p>
<span class="emphasis">
<em>Makes 2 cups </em>
</span>
</p>
<p>Some think Southerners would fry anything if given half a chance. In this case, the
                watermelon rind melts inside the crunchy exterior in a totally unique and fabulous
                way. People will hover over the cook in the kitchen, snitching the delicacy; not
                much will make it to the table. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1/3 cup cornmeal </td>
</tr>
<tr>
<td>1/3 cup all-purpose flour </td>
</tr>
<tr>
<td>1 teaspoon salt </td>
</tr>
<tr>
<td>Freshly ground black pepper </td>
</tr>
<tr>
<td>Shortening or vegetable oil for frying </td>
</tr>
<tr>
<td>2 cups watermelon rind, cubed </td>
</tr>
</tbody></table>
<p>Toss together the cornmeal, flour, salt, and pepper. Meanwhile, heat the oil to 350
                degrees in a heavy skillet. The oil should be deep enough to submerge the watermelon
                cubes. With one hand, roll the still damp cubed rind in the cornmeal mixture. Use a
                slotted spoon to add breaded rind to the hot oil, frying in batches if necessary so
                as not to crowd the cubes in the pan. Fry about 8 to 10 minutes, until lightly
                browned. Drain on paper towels and season to taste with salt and pepper. Serve hot. </p>
<div class="mediaobject" title="Photo of the recipe or step above.">
<img alt="Photo of the recipe or step above." src="images/_LMP30302.jpg"/>
<img alt="Photo of the recipe or step above." src="images/_LMP3034.jpg"/>
<img alt="Photo of the recipe or step above." src="images/_LMP3037.jpg"/>
</div>
<div class="sidebar">
<p class="title">
<b></b>
</p>
<p>Remove the fruit from the watermelon and reserve for another purpose. Use a sharp
                    knife to separate the dark tough exterior of the rind from the pale green
                    interior. Chop interior into 1-inch cubes and discard outer skin. </p>
</div>
<h2><a id="stuffedtomatoes" xmlns:saxon="http://icl.com/saxon"></a>Stuffed Grape
                Tomatoes </h2>
<p>
<span class="emphasis">
<em>Makes 1 pint </em>
</span>
</p>
<p>Grape and other small tomatoes have changed the party table. They are very perky and
                just the right size for a bite. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1 pint grape tomatoes </td>
</tr>
<tr>
<td>4 ounces soft goat cheese, room temperature </td>
</tr>
<tr>
<td>Basil or oregano leaves for garnish </td>
</tr>
</tbody></table>
<p>Make a cup out of each tomato by cutting the tops off and removing the seeds with a
                small spoon. Reserve tops. Cut a small slice off the bottom of each tomato so it
                will stand without falling over. Drain tomatoes upside-down on a rack over a paper
                towel. </p>
<p>Cut off one corner of a plastic ziplock bag to make a piping bag. A star or other
                pastry tip may be inserted if a decorative top is desired. Add goat cheese to bag.
                Turn the tomatoes right side up and move the slit end of the bag over the small
                tomato cavity. Push from the top of the bag, using the other hand to steady the bag.
                Pipe the goat cheese into the tomatoes, coming up slightly over the edge. This may
                be made ahead of time and stored in the refrigerator for up to 1 day. Cover with the
                tops and garnish with basil or oregano leaves before serving. </p>
<p><span class="bold"><strong>Variation:</strong></span> The fillings are endless,
                including ham or <span class="link" href="#/ch05#chickensalad" title="Link target outside of Sample">Basic Chicken Salad</span> or <a class="link" href="#/ch04#pimento" title="Pimento Cheese">Pimento Cheese</a>. </p>
<div class="mediaobject" title="Photo of the recipe or step above.">
<img alt="Photo of the recipe or step above." src="images/_IIP9947.jpg"/>
<img alt="Photo of the recipe or step above." src="images/_IIP9945.jpg"/>
<img alt="Photo of the recipe or step above." src="images/_IIP0076.jpg"/>
<img alt="Photo of the recipe or step above." src="images/_IIP0090.jpg"/>
</div>
<h2><a id="d0e908" xmlns:saxon="http://icl.com/saxon"></a>Indispensable Mushrooms with
                Greens </h2>
<p>
<span class="emphasis">
<em>Serves 16 </em>
</span>
</p>
<p>Party food should be pretty, easy to assemble, and easy to serve, as well as
                delicious. Button mushrooms fit the bill perfectly. They can be made ahead and
                reheated easily and filled with anything from beef stew to foie gras. These button
                mushrooms can be eaten with one bite, but please do serve with napkins if not a
                plate. Large mushrooms will need a plate and should be served at a sit-down meal.
                Any leftover filling can be frozen for another time, or it can be added to rice,
                couscous, or a Sunday omelet. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>3/4 pound fresh spinach or baby turnip greens, <span class="link" href="#/ch07#washinggreens" title="Link target outside of Sample">washed</span>
</td>
</tr>
<tr>
<td>2 pounds small mushrooms, cleaned </td>
</tr>
<tr>
<td>1 cup butter, divided </td>
</tr>
<tr>
<td>2 medium onions, chopped </td>
</tr>
<tr>
<td>3 garlic cloves, chopped </td>
</tr>
<tr>
<td>1/2 cup fine breadcrumbs </td>
</tr>
<tr>
<td>1/2 teaspoon Dijon mustard </td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>Freshly ground black pepper </td>
</tr>
<tr>
<td>1/2 cup grated Parmesan cheese </td>
</tr>
</tbody></table>
<p>Preheat oven to 350 degrees. </p>
<p>Remove and discard any stems and tough leaves from the greens. Chop the leaves in a
                food processor. If large, blanch briefly and drain well. Remove stems of the
                mushrooms, chop, and set aside. </p>
<p>Melt 1/2 cup butter in a Dutch oven or deep skillet over medium heat, and dip the
                mushroom caps into it until well coated on all sides. Place them, top side down, on
                a greased rimmed baking sheet. </p>
<p>Heat remaining butter in the skillet; add the onions, garlic, and chopped mushroom
                stems, and sauté until very soft, about 10 minutes. Add the greens and cook a few
                minutes until wilted; drain off and reserve some of the liquid. Add the breadcrumbs
                and mustard, and mix well. Add back some of the reserved liquid if the mixture is so
                dry that it doesn’t stick together easily. This filling can be made several days in
                advance or frozen and defrosted before stuffing mushrooms. Season to taste with salt
                and pepper. </p>
<p>Fill each buttered mushroom cap (up to a day in advance) with this mixture, mounding
                it high, and sprinkle with Parmesan cheese. Bake 10 to 15 minutes. Any leftovers
                freeze fine for the family, but not for company. </p>
<p><span class="bold"><strong>Variation:</strong></span> Add 1/2 cup finely chopped ham,
                crisp bacon, sautéed sausage, chopped shrimp, crabmeat, or smoked turkey. Garnish
                with chopped herbs or small whole shrimp.</p>
<h2><a id="d0e955" xmlns:saxon="http://icl.com/saxon"></a>Julia’s Kale Chips </h2>
<p>
<span class="emphasis">
<em>Makes about 2 cups </em>
</span>
</p>
<p>Once you try these baked kale chips, you’ll wonder why you waste your time snacking
                on any other chip. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1 bunch kale, washed, stems removed </td>
</tr>
<tr>
<td>1 teaspoon salt </td>
</tr>
<tr>
<td>1/2 teaspoon freshly ground black pepper </td>
</tr>
<tr>
<td>1 tablespoon oil, cook’s preference </td>
</tr>
</tbody></table>
<p>Preheat oven to 400 degrees. </p>
<p>Tear the kale into bite-sized pieces and toss with the salt, pepper, and oil.
                Depending on quantity, spread out in a single layer on one or two rimmed baking
                sheets. </p>
<p>Bake approximately 10 minutes. Keep a close watch to see that the pieces have crisped
                without browning too much. If the kale has been layered on the baking sheet too
                much, it will take an extra minute or two. </p>
<p>When the chips are finished, remove from the oven and serve immediately. These do not
                keep well, so enjoy them in one sitting. </p>
<p><span class="bold"><strong>Variation:</strong></span> I enjoy the stems tossed in oil
                and baked as above for a cook’s treat. </p>
<h2><a id="d0e985" xmlns:saxon="http://icl.com/saxon"></a>Sautéed Zucchini or Squash
                Blossoms </h2>
<p>
<span class="emphasis">
<em>Serves 4 </em>
</span>
</p>
<p>Squash and zucchini, like okra, have particularly tasty blossoms. The male flowers do
                not traditionally bear any “fruit,” but they are mouthwatering stuffed and
                deep-fried in a <span class="link" href="#/ch12#batter" title="Link target outside of Sample">batter</span> or used as a pretty, flavorful garnish. The female flowers can be
                distinguished by the tiny vegetable protruding from the flower. The flower withers
                as the fruit grows. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>2 tablespoons butter </td>
</tr>
<tr>
<td>1 pint (about 12) zucchini flower blossoms, lightly rinsed and dried </td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>Freshly ground black pepper </td>
</tr>
<tr>
<td>1 tablespoon chopped fresh thyme or basil </td>
</tr>
</tbody></table>
<p>Heat the butter in a large skillet. Add the squash flowers and cook for just a few
                minutes, turning until they burst into full blossoms without browning. Season to
                taste with salt, pepper, and herbs. Serve immediately.</p>
<h2><a id="d0e1010" xmlns:saxon="http://icl.com/saxon"></a>Stuffed Zucchini or Squash
                Blossoms </h2>
<p>
<span class="emphasis">
<em>Makes 12 blossoms </em>
</span>
</p>
<p>Stuffing squash blossoms with this recipe of my intern Chuck Lee and frying them in
                light batter results in just a kiss of crispness in the blossoms, with a melting
                interior. This recipe can be increased as desired. Refrigerate the leftovers for the
                next day. Any extra stuffing can be refrigerated and used as a stuffing for grape or
                cherry tomatoes. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>2 tablespoons oil, cook’s preference </td>
</tr>
<tr>
<td>1/2 medium onion, finely chopped </td>
</tr>
<tr>
<td>1 garlic clove, finely chopped </td>
</tr>
<tr>
<td>1 cup goat or cream cheese or other soft variety, room temperature</td>
</tr>
<tr>
<td>2 tablespoons chopped <span class="link" href="#/ch07b#roastedtomatoes" title="Link target outside of Sample">Oven-Roasted Tomatoes</span></td>
</tr>
<tr>
<td>12 squash blossoms </td>
</tr>
<tr>
<td>
<span class="link" href="#/ch12#batter" title="Link target outside of Sample">Light
                            Batter</span>
</td>
</tr>
<tr>
<td>Shortening or vegetable oil for frying </td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>Freshly ground black pepper </td>
</tr>
</tbody></table>
<p>Heat the oil in a skillet until shimmering; add the onion and sauté 4 to 5 minutes.
                Add garlic and stir another 30 seconds. Remove from heat and set aside. </p>
<p>Beat cheese with an electric hand mixer until smooth. Stir in the onion and garlic
                mixture, and tomatoes until blended. Move mixture to a plastic ziplock bag. Cut off
                one corner to make a piping bag. Carefully open the squash blossoms and move the
                slit end of the bag into the blossom. Push from the top of the bag and pipe the
                mixture into the blossom to within 1/4 to 1/2 inch from the opening. Gently twist
                the top of the blossom closed. Dip into batter. </p>
<p>Meanwhile, fill a heavy frying pan no more than half full of oil and heat to 350
                degrees. Add the blossoms one by one and fry until light brown, reducing heat if
                they brown too quickly. Move to a paper towel to drain. Season to taste with salt
                and pepper. Serve hot. </p>
<h2><a id="d0e1049" xmlns:saxon="http://icl.com/saxon"></a>Zucchini Rounds with Carrots </h2>
<p>
<span class="emphasis">
<em>Makes 12 to 15 </em>
</span>
</p>
<p>Raw grated carrots piled on zucchini rounds make a unique, colorful, and very
                refreshing appetizer, moving us toward healthy snacks with local foods. This is
                pretty enough for either a buffet table or for passing at a party. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>2 tablespoons olive oil </td>
</tr>
<tr>
<td>2 teaspoons red or white wine vinegar or fresh lemon juice </td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>Freshly ground black pepper </td>
</tr>
<tr>
<td>Granulated sugar to taste </td>
</tr>
<tr>
<td>2 medium carrots, peeled and grated </td>
</tr>
<tr>
<td>1 medium zucchini, unpeeled and thickly sliced </td>
</tr>
<tr>
<td>3 heaping tablespoons finely chopped fresh basil, thyme, oregano, parsley,
                        or other mixed herbs </td>
</tr>
</tbody></table>
<p>Whisk together the olive oil, vinegar or lemon juice, salt, and pepper. Taste and add
                sugar if needed to smooth out the taste. </p>
<p>Chop the grated carrots slightly if necessary to be sure they will fit on the rounds.
                Toss the carrots lightly with the dressing, shaking off excess liquid. Mound the
                dressed carrots on the zucchini slices, garnish with fresh herbs, and chill. May be
                made up to 1 day ahead, covered tightly, and kept chilled. </p>
<p><span class="bold"><strong>Variation:</strong></span> Substitute cucumber for the
                zucchini.</p>
<h2><a id="d0e1083" xmlns:saxon="http://icl.com/saxon"></a>Lady Pea Patties </h2>
<p>
<span class="emphasis">
<em>Makes 24 small patties </em>
</span>
</p>
<p>Hands are enough implements for eating these tasty patties. Serve from the pan, stack
                on the buffet table, or pass with napkins. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>3 cups cooked and drained lady peas or other small peas or beans </td>
</tr>
<tr>
<td>1 large egg, lightly beaten </td>
</tr>
<tr>
<td>1 teaspoon chopped fresh thyme </td>
</tr>
<tr>
<td>1 tablespoon chopped fresh basil </td>
</tr>
<tr>
<td>1 tablespoon chopped fresh oregano </td>
</tr>
<tr>
<td>1 small red onion, chopped </td>
</tr>
<tr>
<td>2 garlic cloves, chopped </td>
</tr>
<tr>
<td>1 green onion, chopped </td>
</tr>
<tr>
<td>1/2 jalapeño pepper or other small hot pepper, roasted, peeled, seeded, and
                        finely chopped </td>
</tr>
<tr>
<td>All-purpose flour </td>
</tr>
<tr>
<td>Shortening or vegetable oil for frying </td>
</tr>
<tr>
<td><span class="link" href="#/ch16#norman" title="Link target outside of Sample">Norman’s
                            Rémoulade</span> or <span class="link" href="#/ch05#redmayonnaise" title="Link target outside of Sample">Red Pepper Mayonnaise</span>
</td>
</tr>
</tbody></table>
<p>Line a rimmed baking sheet with parchment or waxed paper and set aside. </p>
<p>Mash the peas or beans with a fork in a large mixing bowl. Mix in the egg, thyme,
                basil, oregano, onion, garlic, green onion, and desired amount of jalapeño pepper. </p>
<p>Form the mixture by hand into 24 small patties and move to prepared baking sheet.
                Sprinkle the patties lightly with flour on both sides. </p>
<p>Coat the bottom of a large skillet with oil and heat until shimmering. Cooking in
                batches, cook patties about 1-1/2 to 2 minutes on the first side. Turn with a
                spatula and cook another 1-1/2 to 2 minutes on the second side. Remove to a warm
                serving platter. </p>
<p>Serve hot with Rémoulade Sauce or Red Pepper Mayonnaise. </p>
<p><span class="bold"><strong>Variation:</strong></span> Any cooked peas can be made
                into a similar patty, so don’t feel “locked in” to lady peas just because they are
                my favorite! </p>
<h2><a id="d0e1136" xmlns:saxon="http://icl.com/saxon"></a>Coca-Cola-Glazed Wings </h2>
<p>
<span class="emphasis">
<em>Serves 6 to 8 as a starter </em>
</span>
</p>
<p>This recipe adapted from Virginia Willis’ <span class="emphasis"><em>Bon Appétit,
                        Y’all</em></span> is an outdoors or use-your-hands-nosh. The exact measures
                are not crucial. Start with fewer hot peppers rather than more. Virginia uses Scotch
                Bonnets. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1 cup Coca-Cola </td>
</tr>
<tr>
<td>1/2 cup fresh lime juice or apple cider vinegar </td>
</tr>
<tr>
<td>1-1/2 cups light or dark brown sugar </td>
</tr>
<tr>
<td>1 to 3 red jalapeños or other small hot peppers, finely chopped </td>
</tr>
<tr>
<td>3 pounds chicken wings </td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>Freshly ground black pepper </td>
</tr>
</tbody></table>
<p>Bring the Coca-Cola, lime juice or vinegar, brown sugar, and peppers to the boil in a
                small saucepan. Reduce heat to medium-low and simmer about 30 minutes, until syrupy,
                and set aside to cool slightly. Prepare the wings by cutting off the tips (reserving
                them for <span class="link" href="#/ch06#stocks" title="Link target outside of Sample">stock</span> if
                desired) and cutting the wings in half at the joint with a sharp knife. Move the
                wings to a large plastic ziplock bag and season to taste with salt and pepper. Pour
                half the cooled glaze over the wings and stir to coat. Reserve remaining glaze. </p>
<p>When ready to cook, preheat the broiler. Reheat reserved glaze over low heat in a
                small saucepan. Line a rimmed baking sheet with aluminum foil and move a rack atop
                the foil. Move the wings to the rack. Broil the wings in the oven 4 inches from the
                heat for about 10 minutes. Turn the wings over to the other side using tongs, brush
                with reserved glaze, and broil 10 minutes. Remove from oven, move to a warm platter,
                and serve immediately. </p>
<p><span class="bold"><strong>Variation:</strong></span> This can be a main course for
                fewer people.</p>
<p>Coca-Cola is not only a Southern drink, it is used for cooking, its sweeteners
                caramelizing and transforming the mundane. </p>
<h2><a id="d0e1176" xmlns:saxon="http://icl.com/saxon"></a>Grilled Chicken Saté </h2>
<p>
<span class="emphasis">
<em>Makes 8 skewers </em>
</span>
</p>
<p>Skewered chicken pieces marinated in a snappy sauce are ideal for snacking on while
                waiting for the grill to finish cooking the main course. They can be served with
                vegetables and turned into a main course for fewer people. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>2 pounds boneless skinless chicken breast, cut into 1-inch cubes </td>
</tr>
<tr>
<td>1/2 cup soy sauce </td>
</tr>
<tr>
<td>1 teaspoon finely chopped or grated fresh ginger </td>
</tr>
<tr>
<td>2 tablespoons dark sesame oil </td>
</tr>
<tr>
<td>1 recipe <span class="link" href="#/ch16#gingerpeanutsauce" title="Link target outside of Sample">Ginger Peanut Sauce</span></td>
</tr>
</tbody></table>
<p>If using wooden skewers, soak in water until needed. </p>
<p>Move chicken breast pieces to a plastic ziplock bag. Add soy sauce, ginger, and oil.
                Marinate 30 minutes or up to 8 hours. </p>
<p>When ready to grill, thread the chicken onto small presoaked wooden skewers and move
                to a hot grill or a rimmed baking sheet 6 inches from the hot broiler. Grill or
                broil 3 to 4 minutes; turn and cook the second side another 2 to 4 minutes, until
                the chicken registers 165 degrees on an instant-read thermometer. Remove from grill
                or broiler. Serve hot on skewers, or remove chicken from skewers to a serving bowl
                and serve with toothpicks. Serve with Ginger Peanut Sauce for dipping. </p>
<p>
<span class="bold">
<strong>Variations:</strong>
</span>
</p>
<div class="itemizedlist">
<ul class="itemizedlist">
<li class="listitem">
<p>Shrimp skewers are equally tasty. </p>
</li>
<li class="listitem">
<p>The chicken breasts may be cut into strips and threaded onto the skewers.
                            As the strips will be thinner, adjust cooking time accordingly. </p>
</li>
</ul>
</div>
<h2><a id="d0e1216" xmlns:saxon="http://icl.com/saxon"></a>Pork and Peanut Hors
                d’oeuvres </h2>
<p>
<span class="emphasis">
<em>Makes 6 to 8 skewers </em>
</span>
</p>
<p>Pork and peanuts are a very compatible couple. It’s the double shot of protein that
                makes this dish so helpful as an hors d’oeuvre, keeping guests from being ravenous
                while waiting for dinner. This can also be used as a main course for four. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1 cup shelled, roasted, and salted peanuts </td>
</tr>
<tr>
<td>1 bunch green onions, both green and white parts, chopped to make 3/4 cup
                    </td>
</tr>
<tr>
<td>Juice of 1 lime </td>
</tr>
<tr>
<td>2 tablespoons light or dark brown sugar </td>
</tr>
<tr>
<td>1/3 cup soy sauce </td>
</tr>
<tr>
<td>1 teaspoon chopped fresh ginger </td>
</tr>
<tr>
<td>3 garlic cloves, chopped </td>
</tr>
<tr>
<td>1 tablespoon coriander seeds </td>
</tr>
<tr>
<td>2 tablespoons hot red peppers, fresh or dried </td>
</tr>
<tr>
<td>1/2 cup beef stock or broth </td>
</tr>
<tr>
<td>1/4 cup butter, melted </td>
</tr>
<tr>
<td>1-1/2 pounds lean pork, cut into 1-inch cubes </td>
</tr>
</tbody></table>
<p>If using wooden skewers, soak in water until needed. </p>
<p>Purée the peanuts, onions, lime juice, brown sugar, soy sauce, ginger, garlic,
                coriander seeds, and peppers in a blender or food processor until smooth. Add the
                broth and butter, and purée until well blended. Pour over the cut-up pork and
                marinate 8 hours or more in the refrigerator, covered. </p>
<p>Drain, reserving the marinade. Preheat a broiler or grill. Thread the meat onto
                presoaked skewers or place loose on a wire rack in a single layer; grill or broil 6
                inches from the heat. Cook on one side until crisp and brown, about 5 minutes. Turn,
                brush with the marinade, and brown the other side. Remove to a warm serving platter. </p>
<p>Bring the reserved marinade to the boil, reduce heat to simmer, and cook 2 to 3
                minutes. Serve the pork hot on the skewers, or remove to a platter or bowl and serve
                with toothpicks. Use the hot marinade as a dipping sauce. </p>
<p><span class="bold"><strong>Variation:</strong></span> Shrimp or chicken pieces may be
                substituted, adjusting cooking time accordingly.</p>
<h2><a id="d0e1262" xmlns:saxon="http://icl.com/saxon"></a>Spicy Sausage Balls </h2>
<p>
<span class="emphasis">
<em>Makes 100 to 120 balls </em>
</span>
</p>
<p>This makes a gracious plenty, but the recipe can be cut down proportionally quite
                easily. Any extra sausage balls can be frozen cooked or uncooked. Chilling the dough
                makes it easier to work with. The self-rising flour gives it the puff that makes it
                special. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1 pound hot pork sausage </td>
</tr>
<tr>
<td>1 pound grated sharp Cheddar cheese </td>
</tr>
<tr>
<td>1 cup <span class="link" href="#/ch12#selfrise" title="Link target outside of Sample">self-rising flour</span></td>
</tr>
</tbody></table>
<p>Preheat oven to 400 degrees. </p>
<p>Mix together sausage, cheese, and flour with hands to form a dough, or move to a
                plastic ziplock bag to massage the ingredients together to form a well-incorporated
                dough, and refrigerate 20 to 30 minutes. </p>
<p>Divide the dough into 4 pieces. Working with one piece at a time and keeping the
                remainder refrigerated, take 1 tablespoon of cold sausage mixture and using hands or
                a melon baller, roll it into a 3/4-inch ball. Move to a rimmed baking sheet, leaving
                a small space between balls. Repeat with remaining dough. </p>
<p>Bake 15 minutes. Balls will puff slightly when cooked. Remove pan carefully from the
                oven, as there will be hot fat on the pan. Discard the fat and serve the sausage
                balls immediately. They can be refrigerated overnight and reheated, or wrapped well
                and frozen.</p>
<div class="sidebar">
<p class="title">
<b></b>
</p>
<h4><a id="d0e1289" xmlns:saxon="http://icl.com/saxon"></a>Evaluating Sausage
                    Seasoning </h4>
<p>Usually sausage has a sufficient amount of seasoning, but it’s always a good idea
                    to sauté or microwave a small amount of sausage to know how it tastes before
                    proceeding to add other ingredients. It might need more salt, pepper, or spices,
                    depending on your taste. </p>
</div>
<h2><a id="d0e1293" xmlns:saxon="http://icl.com/saxon"></a>Baby Crab Cakes </h2>
<p>
<span class="emphasis">
<em>Makes 16 crab cakes </em>
</span>
</p>
<p>Pat Conroy is a dear friend of ours and former cooking student of mine; he has
                written the foreword to this book. His crab cake recipe is famous, having appeared
                in the <span class="emphasis"><em>Washington Post</em></span> and in his <span class="emphasis"><em>Pat Conroy Cookbook.</em></span> This adaptation is for
                bite-sized crab cakes that melt in your mouth. Made larger and cooked longer, they
                are equally appetizing as a main course. When cooking a larger cake, surround it
                with a poached egg ring to hold the ingredients together before turning to cook the
                second side. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1 pound lump crabmeat, picked over and cleaned, all shell fragments removed
                    </td>
</tr>
<tr>
<td>1 egg white, lightly beaten </td>
</tr>
<tr>
<td>1 tablespoon all-purpose flour </td>
</tr>
<tr>
<td>2 tablespoons finely chopped fresh chives </td>
</tr>
<tr>
<td>1 teaspoon freshly ground black pepper </td>
</tr>
<tr>
<td>1/4 teaspoon ground hot red pepper </td>
</tr>
<tr>
<td>2 teaspoons coarse salt, divided </td>
</tr>
<tr>
<td>3 tablespoons butter, divided </td>
</tr>
<tr>
<td>3 teaspoons oil, cook’s preference, divided </td>
</tr>
<tr>
<td>Lemon wedges, optional </td>
</tr>
</tbody></table>
<p>Move the crabmeat to a large bowl and pour the egg white over the crabmeat in a slow
                stream, stopping occasionally to mix. When the crabmeat has absorbed the egg and
                feels slightly sticky to the touch, sift the flour over the crabmeat. Sprinkle with
                chives, peppers, and 1 teaspoon salt. Stir lightly from the bottom, but don’t
                overhandle. </p>
<p>Separate the crabmeat into 16 cakes by gently rolling each portion in a flattened
                palm. Gently press down to form the cake and move to a large plate. Sprinkle with
                remaining salt. Refrigerate for 1 hour. </p>
<p>Melt half of the butter and half of the oil in a large skillet. Move 8 of the crab
                cakes to the hot fat and cook until a crust forms, about 2 minutes. Turn carefully
                with a thin metal or plastic spatula and cook another 2 minutes. Remove to a plate
                and cover loosely with foil. Wipe out the skillet with a paper towel and melt the
                remaining butter and oil. Cook remaining crab cakes in the same fashion. Serve hot
                with optional lemon wedges. </p>
<div class="mediaobject" title="Photo of the recipe or step above.">
<img alt="Photo of the recipe or step above." src="images/_LMP0390.jpg"/>
</div>
<h2><a id="d0e1340" xmlns:saxon="http://icl.com/saxon"></a>Herbed Shrimp and Scallop
                Ceviche </h2>
<p>
<span class="emphasis">
<em>Serves 8 </em>
</span>
</p>
<p>Cool shrimp and scallops are enticing in a glass bowl at a buffet. They can also be
                served with toothpicks for nibbles or in shells or fish plates as a first course for
                a sit-down meal. </p>
<p>We know ceviche was served by Martha Washington in early colonial days (spelled <span class="emphasis"><em>caveach</em></span>) most likely wending its way here from
                the Spaniards in Mexico and Latin America as well as from Barbados and other
                islands. This is easy, fast, and beckoning, and is best made a day or two ahead and
                served chilled. Poaching the seafood very quickly before marinating takes away the
                trepidation some people have about eating “raw” seafood. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1-1/2 pounds large raw shrimp in shells </td>
</tr>
<tr>
<td>1/2 pound raw sea scallops, sliced horizontally into 1/2-inch pieces </td>
</tr>
<tr>
<td>Grated rind of 3 limes, 2 lemons, and 1 orange, no white attached </td>
</tr>
<tr>
<td>1/3 cup fresh lime juice (about 3 limes) </td>
</tr>
<tr>
<td>1/4 cup fresh lemon juice (about 2 lemons) </td>
</tr>
<tr>
<td>1/3 cup fresh orange juice (about 1 orange) </td>
</tr>
<tr>
<td>5 tablespoons chopped fresh parsley, thyme, oregano, and basil </td>
</tr>
<tr>
<td>1/2 small red onion, very finely chopped </td>
</tr>
<tr>
<td>1/4 cup olive oil </td>
</tr>
<tr>
<td>1/2 to 1 teaspoon hot sauce, optional </td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>Freshly ground black pepper </td>
</tr>
<tr>
<td>1 teaspoon commercial seafood seasoning or <span class="link" href="#/ch16#creole" title="Link target outside of Sample">Creole Seasoning</span>,
                        optional </td>
</tr>
<tr>
<td>1 large avocado, peeled and sliced </td>
</tr>
</tbody></table>
<p>Bring 2 quarts of water to the boil in a large pot. Add the shrimp and poach just a
                few minutes, until tender and pink. Remove the shrimp with a slotted spoon or
                strainer, reserving the poaching liquid, and set shrimp aside to cool. </p>
<p>Return the poaching liquid to the boil. Move the scallops to a heatproof strainer or
                colander and dip into the boiling water for about 30 seconds, until the scallops are
                just cooked. Remove and set aside to cool. Peel the cooled shrimp and cut into
                thirds. Move the scallops and shrimp to a plastic ziplock bag. Add the grated lime,
                lemon, orange rinds, and the juices. Refrigerate covered, and marinate overnight,
                tossing occasionally. </p>
<p>When ready to serve, bring to room temperature and drain, reserving the juice. Add
                the herbs, onion, oil, and hot sauce, and toss lightly. Season to taste with salt
                and pepper. Taste and add optional seafood seasoning as desired. Serve chilled with
                the avocado. It looks smashing in a stemmed wide champagne glass with a wedge of
                avocado. </p>
<div class="sidebar">
<p class="title">
<b></b>
</p>
<p><span class="emphasis"><em>Caveach,</em></span> a noun for “pickled mackerel,” is
                    also a verb that refers to the “cooking of raw fish” using lime juice or other
                    acid. Known in early American cookbooks as <span class="emphasis"><em>ceviche</em></span>—also spelled <span class="emphasis"><em>cebiche</em></span> and <span class="emphasis"><em>seviche</em></span>, this acid “cooking” actually brings about a change
                    of appearance and texture associated with cooked fish but, in fact, does not
                    actually cook it. In French, Spanish, and Portuguese, <span class="emphasis"><em>escabeche</em></span> is a similar term but refers to pickling of
                    cooked fish. </p>
</div>
<h2><a id="d0e1409" xmlns:saxon="http://icl.com/saxon"></a>Shrimp and Cheese Grits
                Custards with Green Sauce </h2>
<p>
<span class="emphasis">
<em>Serves 4 to 6 </em>
</span>
</p>
<p>These rich nuggets of beauty and taste unmold easily from ramekins, nonstick molds,
                or timbales, perfect for times when a smashing starter is required. The sauce can be
                made up to a day or two ahead and refrigerated, but the custards are best made just
                before serving. The greens add a tart piquancy and dark green beauty to the sauce.
                Other grits, such as stone-ground, are welcome but take longer to cook. For more
                information on custards, see <span class="link" href="#/ch15#custards" title="Link target outside of Sample">here</span>. </p>
<h4><a id="d0e1421" xmlns:saxon="http://icl.com/saxon"></a>Grits Custards </h4>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1 cup water </td>
</tr>
<tr>
<td>1 cup milk </td>
</tr>
<tr>
<td>1/2 cup uncooked quick grits </td>
</tr>
<tr>
<td>7 ounces soft goat cheese </td>
</tr>
<tr>
<td>1 large egg, beaten to mix </td>
</tr>
<tr>
<td>2 tablespoons finely chopped fresh basil, thyme, or oregano </td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>Freshly ground black pepper </td>
</tr>
</tbody></table>
<h4><a id="d0e1440" xmlns:saxon="http://icl.com/saxon"></a>Green Sauce </h4>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1/2 cup heavy cream </td>
</tr>
<tr>
<td>1/2 cup sour cream </td>
</tr>
<tr>
<td>10 ounces baby spinach or turnip greens, <span class="link" href="#/ch07#washinggreens" title="Link target outside of Sample">washed</span>
</td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>Freshly ground pepper </td>
</tr>
<tr>
<td>1-1/2 pounds shrimp, <span class="link" href="#/ch09#cookshrimp" title="Link target outside of Sample">cooked and
                            peeled</span>
</td>
</tr>
<tr>
<td>2 tablespoons chopped basil, thyme, or oregano </td>
</tr>
</tbody></table>
<p><span class="bold"><strong>For the Grits Custards:</strong></span> Preheat oven to
                350 degrees. Generously butter 4 to 6 (1/2-cup) ramekins, molds (preferably
                nonstick), or timbales. To ease removal, put a piece of parchment paper on the
                bottom of the ramekin and butter it as well. </p>
<p>Heat the water and milk carefully in a heavy nonstick saucepan until there is a small
                boil around the edge of the pan. Slowly stir in the grits. Cover and cook over
                medium-low heat, stirring occasionally, for 10 to 15 minutes, until the grits are
                tender and creamy or cook according to package directions. Add more liquid as
                needed. Stir in the cheese, egg, and herbs, and season to taste with salt and
                pepper. Spoon into prepared ramekins. </p>
<p>Fold a tea towel on the bottom of a large roasting pan and place ramekins on top of
                the towel. Pour boiling water into the pan so that it reaches halfway up the sides
                of the ramekins, being careful not to get any water into the custard mixture. Move
                the roasting pan to the center of the oven and bake approximately 30 minutes, or
                until the custard is set and a knife inserted in the center comes out clean. Do not
                let the water boil, as the boiling will overcook the custard and create holes in it;
                if necessary, add cold water to the pan to prevent bubbling. Remove the pan from the
                oven; remove the ramekins from the pan. Cool slightly before unmolding. </p>
<p><span class="bold"><strong>For the Green Sauce: </strong></span>Add the cream and
                sour cream to a heavy 4-cup saucepan. Bring to the boil around the edges of the pan,
                taking care that it does not boil over. Reduce heat and simmer until the liquid is
                cooked down by about half, about 10 minutes. </p>
<p>Wash the greens, draining well. Remove and discard any large veins. When the cream
                has reduced, add the greens and let wilt briefly. Purée with an immersion blender or
                move to a blender and process until well puréed and smooth. Season to taste with
                salt and pepper. </p>
<p>Toss the cooked shrimp with the herbs. Run a knife around the edge of the custards
                and unmold the custards onto individual serving plates, remove the parchment paper,
                spoon the sauce around them, and distribute the shrimp evenly among the plates. </p>
<p><span class="bold"><strong>Variation:</strong></span> Blanch very large turnip greens
                briefly and drain well before adding to the cream.</p>
<div class="sidebar">
<p class="title">
<b></b>
</p>
<p>Cooking grits is most easily done in the microwave, using a large heat-proof
                    measuring cup, stirring every 5 minutes until cooked. </p>
</div>
<h2><a id="d0e1486" xmlns:saxon="http://icl.com/saxon"></a>Bacon and Basil “Angels on
                Horseback” </h2>
<p>
<span class="emphasis">
<em>Serves 6 </em>
</span>
</p>
<p>When I was younger, the “angels on horseback” served at my parents’ parties were
                marinated bacon-wrapped oysters. The South’s plump oysters and the basil taste
                amazingly fresh and vibrant and make a good transition from the end of our basil
                season in the late fall to more robust winter oysters. The crispy bacon adds a final
                touch. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>24 shucked fresh oysters </td>
</tr>
<tr>
<td>2 tablespoons oil, cook’s preference </td>
</tr>
<tr>
<td>1/4 cup fresh lemon juice </td>
</tr>
<tr>
<td>1 garlic clove, finely chopped </td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>Freshly ground black pepper </td>
</tr>
<tr>
<td>Dash of hot sauce, optional </td>
</tr>
<tr>
<td>12 thin bacon slices </td>
</tr>
<tr>
<td>24 large basil leaves </td>
</tr>
<tr>
<td>6 skewers </td>
</tr>
</tbody></table>
<p>Drain the oysters and set aside. If using wooden skewers or toothpicks, soak in water
                until needed. </p>
<p>Whisk together the oil, lemon juice, garlic, salt, pepper, and hot sauce to taste.
                Add the oysters. Cover and marinate for 30 minutes to 2 hours, stirring once or
                twice. </p>
<p>When ready to cook, preheat the broiler or a charcoal grill. </p>
<p>Meanwhile, partially cook the bacon until lightly brown but still supple enough to
                wrap around the oysters. Cut the bacon slices in half. </p>
<p>Drain the oysters well, discarding the marinade. Wrap each oyster with a basil leaf
                and then with a piece of bacon, securing with a toothpick or threading 4 oysters
                onto each skewer. Grill or broil on a rimmed baking sheet for 3 minutes, or long
                enough to crisp the bacon, turning if needed. </p>
<p>Serve one skewer per person on a plate with a fork, or as individual tidbits from a
                platter or tray, with toothpick intact.</p>
<h2><a id="d0e1528" xmlns:saxon="http://icl.com/saxon"></a>Oyster Fritters </h2>
<p>
<span class="emphasis">
<em>Serves 4 to 6 </em>
</span>
</p>
<p>This recipe is an ideal use for vacuum-packed fresh oysters as shucking them for
                frying is a bit more trouble than one cook can handle. There is no reason these
                tasty morsels can’t be served right from the paper towel to those hanging around in
                the kitchen (who probably won’t wait for the fritters to be plated if they have a
                choice). </p>
<p>Add the oysters to the fryer one by one so they don’t bunch up. When fritters are
                cooked enough to be turned in the oil, they turn easily with just a nudge from a
                metal slotted spoon. If they balk, let them cook a minute more. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>24 shucked fresh oysters and their liquor </td>
</tr>
<tr>
<td>
<span class="link" href="#/ch12#selfrise" title="Link target outside of Sample">self-rising flour</span>
</td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>Freshly ground black pepper </td>
</tr>
<tr>
<td>Shortening or vegetable oil for frying </td>
</tr>
</tbody></table>
<p>Strain the oyster juice into a bowl and set aside the oysters. Season the flour to
                taste with salt and pepper. Stir enough of the seasoned flour mixture into the
                oyster liquor to make batter enough to coat the oysters, adding water if necessary.
                Add the oysters and stir to coat. </p>
<p>Heat 1/4 to 1/2 inch oil in a heavy frying pan until the oil ripples. Carefully add
                the fritters to the frying pan, not touching, in batches. Fry until light brown,
                turn and repeat. Remove to a paper towel and keep warm. Serve hot or on a plate or
                with napkins. Serve with hot sauce for those who must have it. </p>
<p>
<span class="bold">
<strong>Variations:</strong>
</span>
</p>
<div class="itemizedlist">
<ul class="itemizedlist">
<li class="listitem">
<p>Add hot pepper sauce or seafood seasoning to the batter as desired. </p>
</li>
<li class="listitem">
<p>Add to a green salad as a starter or lunch course.</p>
</li>
</ul>
</div>
<div class="sidebar">
<p class="title">
<b></b>
</p>
<p>Oysters vary in saltiness, and it is the cook’s prerogative to taste them before
                    flouring and season the flour appropriately. </p>
</div>
<h2><a id="d0e1571" xmlns:saxon="http://icl.com/saxon"></a>Grilled Skewered Oysters </h2>
<p>
<span class="emphasis">
<em>Serves 4 to 6 </em>
</span>
</p>
<p>The smoky sweetness of oysters benefits from this brief, simple cooking. Served
                alone, they are a worthy appetizer, but they can be “stretched” into a main course
                by serving with rice and vegetables. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1/2 cup butter </td>
</tr>
<tr>
<td>3 garlic cloves, finely chopped </td>
</tr>
<tr>
<td>24 shucked fresh oysters, drained </td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>Freshly ground black pepper </td>
</tr>
<tr>
<td>3 tablespoons chopped fresh parsley and/or thyme and fennel fronds, optional
                    </td>
</tr>
<tr>
<td><span class="link" href="#/ch16#cocktail" title="Link target outside of Sample">Cocktail
                            Sauce</span>, optional </td>
</tr>
</tbody></table>
<p>If using wooden skewers or toothpicks, soak in water until needed. Preheat the grill
                or broiler. </p>
<p>Heat the butter and the garlic in the microwave or a small pan until the butter is
                melted. Toss the oysters in the mixture. Thread oysters onto small presoaked wooden
                skewers and move them to a hot grill or a rimmed baking sheet 6 inches from the hot
                broiler. Cook until the oysters turn white and are starting to curl at the edges,
                about 5 to 8 minutes. </p>
<p>Season to taste with salt and pepper. Sprinkle with the optional chopped herbs. Serve
                with Cocktail Sauce if desired. </p>
<p>Leave the oysters on the skewers for an informal presentation or remove them to
                plates by sliding the oysters off with a fork.</p>
<div class="sidebar">
<p class="title">
<b></b>
</p>
<p>An old wives’ tale says to eat oysters only in the months that have an R in the
                    name (September to April). This wasn’t based on health considerations but on the
                    difficulty of shipping oysters without spoilage in hot weather and on the
                    spawning habits of the oyster and changes accompanying the process: oysters are
                    drier and less tasty when spawning. Present-day oyster farming, government
                    regulations, refrigeration, and shipping are now of such a high quality that
                    health considerations are not a problem. Still, the environmentally conscious
                    are reluctant to eat spawning oysters, which are the future of the industry, and
                    aficionados disdain them. </p>
</div>
<h2><a id="d0e1608" xmlns:saxon="http://icl.com/saxon"></a>Fried Frog Legs </h2>
<p>
<span class="emphasis">
<em>Serves 6 </em>
</span>
</p>
<p>Louisianans in particular love fried frog legs, and they cook them much as they would
                quail, marinating them with buttermilk. One never knows when one will find oneself
                confronted with frog legs in the market, so we include it here. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>12 frog legs </td>
</tr>
<tr>
<td>4 cups buttermilk </td>
</tr>
<tr>
<td>Hot sauce </td>
</tr>
<tr>
<td>1 large egg </td>
</tr>
<tr>
<td>3 tablespoons Creole mustard </td>
</tr>
<tr>
<td>1 (12-ounce) bottle or can of beer </td>
</tr>
<tr>
<td>2 garlic cloves, chopped </td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>Freshly ground black pepper </td>
</tr>
<tr>
<td>4 cups seasoned corn flour, yellow or white </td>
</tr>
<tr>
<td>Shortening or vegetable oil for frying </td>
</tr>
</tbody></table>
<p>Soak frog legs in buttermilk with hot sauce to taste for 1 hour. </p>
<p>Whisk together the egg, mustard, beer, and garlic. Season to taste with salt, pepper,
                and hot sauce. Move corn flour to a separate bowl. </p>
<p>Heat the oil in a large heavy skillet to 375 degrees. </p>
<p>Remove frog legs from buttermilk, give a shake, and roll in corn flour, using one
                hand. Dip in egg mix, and then again in corn flour. </p>
<p>Meanwhile, heat enough shortening in a deep skillet to cover the frogs legs, taking
                care it is not more than halfway up the pan. Add the legs to the pan skin side down.
                Fry until golden brown, turning if necessary. Remove with tongs and drain on paper
                towels. </p>
<h2><a id="d0e1650" xmlns:saxon="http://icl.com/saxon"></a>Shrimp and Okra Beignets with
                Salsa and Cilantro-Lime Sour Cream </h2>
<p>
<span class="emphasis">
<em>Serves 4 </em>
</span>
</p>
<p>Hominy Grill Restaurant in Charleston makes the most astonishing okra beignets I’ve
                ever eaten. May these be half as good! They will inspire rave reviews. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1 large egg, lightly beaten </td>
</tr>
<tr>
<td>2 tablespoons heavy cream or half-and-half </td>
</tr>
<tr>
<td>3 tablespoons fine dry breadcrumbs </td>
</tr>
<tr>
<td>3 tablespoons all-purpose flour </td>
</tr>
<tr>
<td>1/2 teaspoon salt, or to taste</td>
</tr>
<tr>
<td>1/4 teaspoon freshly ground black pepper, or to taste </td>
</tr>
<tr>
<td>2 cups okra, chopped into 1/3-inch thick pieces </td>
</tr>
<tr>
<td>1/2 cup yellow onion, diced </td>
</tr>
<tr>
<td>1/3 cup green bell pepper, diced</td>
</tr>
<tr>
<td>1 jalapeño pepper or other small hot pepper, seeded and finely chopped </td>
</tr>
<tr>
<td>1/2 pound raw shrimp, peeled and roughly chopped </td>
</tr>
<tr>
<td>Shortening or vegetable oil for frying </td>
</tr>
<tr>
<td>
<span class="link" href="#/ch16#salsa" title="Link target outside of Sample">Salsa</span>
</td>
</tr>
<tr>
<td>1 cup Cilantro-Lime Sour Cream (below) </td>
</tr>
</tbody></table>
<p>Whisk together egg and cream in a small bowl until combined. </p>
<p>Whisk together breadcrumbs, flour, salt, and pepper in a large bowl. Toss okra,
                onion, bell pepper, and jalapeño pepper in breadcrumb mixture. Stir in egg mixture
                until thoroughly combined. Allow batter to rest at room temperature for 20 minutes. </p>
<p>Meanwhile, make the Salsa and Cilantro-Lime Sour Cream. </p>
<p>After batter has rested, stir vigorously for 2 minutes, until it has a gooey
                consistency. Season to taste with salt and pepper. Stir the shrimp into the batter
                until evenly incorporated. </p>
<p>When ready to cook, heat oven to 200 degrees. Move a wire cooling rack to a rimmed
                baking sheet lined with paper towels and set aside. Line a second baking sheet with
                foil and set aside. </p>
<p>Pour oil into a wide 3- to 4-quart heavy pot to a depth of 1-1/2 inches and heat
                until thermometer registers 350 degrees. Carefully add level tablespoons of beignet
                batter to the hot oil one at a time, and fry in batches of 10 to 12 for about 2 to 5
                minutes per batch, until golden brown, turning over once. Remove with a slotted
                spoon to the paper towels, then transfer to the foil-lined baking sheet and move
                into the oven to keep warm. </p>
<p>Serve hot with Salsa and Cilantro-Lime Sour Cream. </p>
<p><span class="bold"><strong>Variation:</strong></span> Substitute 1 (10-ounce) box
                frozen cut okra, thawed, if fresh is not available. </p>
<h3><a id="d0e1708" xmlns:saxon="http://icl.com/saxon"></a>Cilantro-Lime Sour Cream </h3>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>Finely grated rind of 1 lime, no white attached </td>
</tr>
<tr>
<td>Juice of 1/2 lime </td>
</tr>
<tr>
<td>2 tablespoons chopped fresh cilantro </td>
</tr>
<tr>
<td>1 cup sour cream </td>
</tr>
<tr>
<td>Salt to taste </td>
</tr>
</tbody></table>
<p>Stir all ingredients together in a small bowl; cover and refrigerate until serving. </p>
<h2><a id="d0e1723" xmlns:saxon="http://icl.com/saxon"></a>Pickled Shrimp, or “Swimpee” </h2>
<p>
<span class="emphasis">
<em>Makes 2 pounds </em>
</span>
</p>
<p>I couldn’t resist using the name of this recipe from <span class="emphasis"><em>Charleston Receipts.</em></span> It got this name from the vendors who
                early-on walked the streets of Charleston calling out “swimpee” to sell their wares.
                The recipe, however, is my own. This recipe doubles easily. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>2 pounds shrimp, <span class="link" href="#/ch09#cookshrimp" title="Link target outside of Sample">cooked and
                            peeled</span>
</td>
</tr>
<tr>
<td>3 large sweet onions, sliced </td>
</tr>
<tr>
<td>3/4 cup olive oil </td>
</tr>
<tr>
<td>1 cup sherry vinegar </td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>Freshly ground black pepper</td>
</tr>
<tr>
<td>Fresh herbs to taste, such as lemon thyme or lemon balm </td>
</tr>
<tr>
<td>Lemon slices, optional </td>
</tr>
</tbody></table>
<p>Starting with the shrimp, layer shrimp and onions in a large glass bowl. Mix oil and
                vinegar, and season to taste with salt and pepper. Pour this mixture over the shrimp
                and onions, and cover tightly. Refrigerate overnight or up to 1 week. Garnish with
                lemon slices if desired. Serve layered from the bowl with toothpicks, or on small
                plates. If the mixture has become too liquid, spoon off and discard the excess
                liquid carefully before serving.</p>
<h2><a id="d0e1757" xmlns:saxon="http://icl.com/saxon"></a>Fried Clams </h2>
<p>
<span class="emphasis">
<em>Serves 10 to 12 </em>
</span>
</p>
<p>These little fried clams make a tasty appetizer or filling for a bun with mayonnaise
                or its derivative, tartar sauce. If frying other types of seafood along with the
                clams, remember that size matters: large pieces are fried at a bit lower temperature
                and a bit longer than the tiny pieces, which are nearly “in and out” in a jiffy. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>4 dozen cleaned, shucked small clams </td>
</tr>
<tr>
<td>Shortening or vegetable oil for frying </td>
</tr>
<tr>
<td>1 recipe <span class="link" href="#/ch09#lemonbatter" title="Link target outside of Sample">Lemon Batter for Seafood</span>
</td>
</tr>
<tr>
<td>1 recipe <span class="link" href="#/ch16#tartar" title="Link target outside of Sample">Tartar
                            Sauce</span></td>
</tr>
</tbody></table>
<p>Remove the membrane of the clam “neck” by slitting and peeling with a small sharp
                knife. Pat the clams dry. </p>
<p>Heat a large heavy pot no more than two-thirds full of the oil to between 360 and 375
                degrees. Using a fork or tongs, pick up a few clams (half dozen), dip them into the
                batter, then slide them carefully into the hot fat (rather than dropping from a
                distance), watching out for spatters. As the battered clams begin to turn a light
                golden brown, remove them with a slotted spoon or a round Chinese strainer to a
                rimmed baking sheet lined with paper towels; move to a 200-degree oven to keep warm.
                Repeat until all the clams are fried. Serve with Tartar Sauce or a favorite dip. </p>
<h2><a id="d0e1784" xmlns:saxon="http://icl.com/saxon"></a>Pimento Cheese and Tomato
                Tart </h2>
<p>
<span class="emphasis">
<em>Makes 1 (8- to 9-inch) tart </em>
</span>
</p>
<p>Pimento cheese spread contains mayonnaise, which gives this tart a dainty puff that
                surrounds oven-dried tomatoes. It’s a wonderful throw-together appetizer or luncheon
                dish, reminiscent of a sophisticated pizza. It is important for the pimento cheese
                to be cold when it goes into the oven, hence the chilling instructions. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1 recipe (9-inch) <span class="link" href="#/ch13#piecrust" title="Link target outside of Sample">piecrust</span></td>
</tr>
<tr>
<td>1-1/4 cups <a class="link" href="#/ch04#pimento" title="Pimento Cheese">Pimento Cheese</a>
</td>
</tr>
<tr>
<td>1 pint grape tomatoes </td>
</tr>
</tbody></table>
<p>Prebake the <span class="link" href="#/ch13#prebaking" title="Link target outside of Sample">piecrust</span> in an 8- or
                9-inch tart pan, and set aside to cool. When cool, move to the refrigerator to
                chill. </p>
<p>Spread the Pimento Cheese on the bottom of the piecrust, no more than a third of the
                way up the sides of the tart pan, and chill again. If using a tart pan with a
                removable bottom, surround the bottom of the pan with foil to prevent the filling
                from possibly leaking out while baking. </p>
<p>While the tart is chilling, halve the tomatoes and bake cut side down on an oiled
                rimmed baking sheet at 400 degrees for 20 to 30 minutes, or until shrunken and most
                of the juice has been emitted. Remove and drain. Cool to room temperature. </p>
<p>Place the tomato halves over the pimento cheese, rounded side up; avoid covering
                completely so the cheese can rise above the tomatoes. Chill until ready to bake. </p>
<p>When ready to bake, preheat oven to 350 degrees. Move the filled tart to a rimmed
                baking sheet. Bake 20 minutes, or until puffy. Cool the tart in the pan on a wire
                rack. </p>
<p>
<span class="bold">
<strong>Variations:</strong>
</span>
</p>
<div class="itemizedlist">
<ul class="itemizedlist">
<li class="listitem">
<p>An unbaked crust can be used in a pinch. Watch carefully to be sure the
                            crust gets cooked. Begin cooking the tart at 400 degrees and reduce heat
                            to 350 degrees after the first 15 minutes of baking; cover rim with foil
                            if browning too quickly. </p>
</li>
<li class="listitem">
<p>If no tart pan is to be had, a pie pan may be substituted.</p>
</li>
</ul>
</div>
<div class="sidebar">
<p class="title">
<b></b>
</p>
<h4><a id="d0e1831" xmlns:saxon="http://icl.com/saxon"></a>Is it a pie or a tart? </h4>
<p>A pie can have a top and bottom, or just a bottom crust. A tart rarely, if ever,
                    has a top crust. It is baked in a pan with plain or fluted sides, most often
                    with a removable bottom, and is not as deep as a pie. </p>
</div>
<h2><a id="d0e1835" xmlns:saxon="http://icl.com/saxon"></a>Golden Vidalia Onion Tart
                with Olives and Rosemary </h2>
<p>
<span class="emphasis">
<em>Makes 1 (8- or 9-inch) tart </em>
</span>
</p>
<p>Vidalia onions are amazingly versatile—our Georgia Vidalias being among the world’s
                sweetest onions—and they make for a satisfying tart. But any onion will do—even red
                ones. I also make this free-form, just because it’s faster and easier, and no
                special equipment, like a tart pan with removable bottom, is required. Try
                experimenting with shapes like rectangles and squares so guests don’t just think it
                is an onion pizza but something extraordinarily special, which it is. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1 recipe (9-inch) <span class="link" href="#/ch13#piecrust" title="Link target outside of Sample">piecrust</span></td>
</tr>
<tr>
<td>2 to 3 medium onions, preferably Vidalia, sliced 1/2 inch thick </td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>2 to 3 tablespoons butter or oil, cook’s preference </td>
</tr>
<tr>
<td>1 cup grated Gruyère, Cheddar, or soft goat cheese</td>
</tr>
<tr>
<td>1 to 2 sprigs fresh rosemary, leaves stripped and chopped </td>
</tr>
<tr>
<td>Freshly ground black pepper </td>
</tr>
<tr>
<td>1/2 cup Greek or other black olives </td>
</tr>
</tbody></table>
<p>Prebake the <span class="link" href="#/ch13#prebaking" title="Link target outside of Sample">piecrust</span> in an 8- or
                9-inch tart pan, and set aside to cool. </p>
<p>Meanwhile, prepare the onions. Heat the butter in a large heavy skillet and cook the
                onions slowly over low heat, stirring frequently, until they are <span class="link" href="#/ch07b#caramelized" title="Link target outside of Sample">caramelized</span> and deep
                mahogany brown. This should take about 30 minutes. </p>
<p>Layer the onions and cheese into the tart crust, beginning with the onions and ending
                with the cheese. Sprinkle with rosemary and pepper. Lay the olives on a counter and
                swack them with the side of a large knife to loosen the pits. Remove the pits, chop
                the olives if desired, and sprinkle them on top of the rosemary and cheese. Move to
                a rimmed baking sheet. </p>
<p>When ready to bake, preheat oven to 400 degrees. Bake 10 to 20 minutes, or until
                cheese is melted and the crust is a solid brown. Remove from oven. Serve hot or
                cold. </p>
<p><span class="bold"><strong>Variation:</strong></span> For a delicate layer of onions
                and pastry, roll the dough into an 11-inch-long rectangle and bake it <span class="link" href="#/ch13#freeform" title="Link target outside of Sample">free-form</span>. Sprinkle onions only a
                couple of layers thick. Cut bite-size or larger.</p>
<div class="sidebar">
<p class="title">
<b></b>
</p>
<h4><a id="d0e1885" xmlns:saxon="http://icl.com/saxon"></a>Pitting Olives </h4>
<p>To pit olives, place on a cutting board and swack down on them once or twice
                    using the flat side of a knife blade and a fist. The pits are then easily
                    removed by hand. </p>
<p>Pastry always makes a splash at a party, particularly if it is crisp and flaky
                    and the filling is a stand-out, with enough of a twist that it is clear it isn’t
                    from the grocer’s freezer section. Although any tart can be made into mini-tarts
                    or tartlets, a larger tart has the added advantage of giving substantial
                    portions, particularly helpful for serve-in-the-living-room dinner parties with
                    a small plate and fork. And it is much faster to whip up. While we have complete
                    instructions in the “Pastry and Pies” chapter for making and pre-baking, we also
                    confess that starter tarts—to be consumed quickly by those who have a drink in
                    the other hand—are fine uses for grocery store pastry, particularly the name
                    brand doughs sold rolled up in a box. Avoid frozen premade pie shells in a pan.
                    A free-form tart is a better alternative. </p>
</div>
<h2><a id="d0e1891" xmlns:saxon="http://icl.com/saxon"></a>Bacon and Onion Pie </h2>
<p>
<span class="emphasis">
<em>Makes 1 (9-inch) pie </em>
</span>
</p>
<p>Any savory (non-sweet) piecrust will do for this simple quiche-like pie. The trick is
                making the Flavored Milk, which gives the pie an extraordinary flavor boost. It can
                be omitted when time is of the essence but is worth doing whenever possible. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1 recipe (9-inch) <span class="link" href="#/ch13#piecrust" title="Link target outside of Sample">piecrust</span></td>
</tr>
<tr>
<td>6 slices bacon, cooked crisp and crumbled, drippings reserved </td>
</tr>
<tr>
<td>1 medium onion </td>
</tr>
<tr>
<td>1 large egg </td>
</tr>
<tr>
<td>1 egg yolk</td>
</tr>
<tr>
<td>2 cups grated Gruyère, Cheddar, or soft goat cheese </td>
</tr>
<tr>
<td>1 cup milk, preferably <span class="link" href="#/ch16#milk" title="Link target outside of Sample">Flavored Milk</span>
</td>
</tr>
</tbody></table>
<p>Prebake the piecrust in a 9-inch pie pan and set aside to cool. </p>
<p>Preheat oven to 350 degrees. </p>
<p>Reheat 2 tablespoons bacon drippings in a large skillet; add onion and sauté until
                soft. Lightly beat the egg and egg yolk together in a medium bowl; add milk,
                crumbled bacon, onions, and cheese; stir together. Ladle mixture into a partially
                prebaked piecrust, move to a rimmed baking sheet and bake for 30 to 35 minutes, or
                until a knife inserted in the center of the pie comes out clean. Remove from oven
                and allow to rest for 5 to 10 minutes before serving. </p>
<p><span class="bold"><strong>Variation:</strong></span>Omit the bacon and sauté 2
                additional onions. </p>
<div class="sidebar">
<p class="title">
<b></b>
</p>
<p>Save drippings from bacon, sausage, and other flavorful meats and refrigerate for
                    use in other recipes. If none are available, any fat, such as butter or oil, can
                    be substituted in equal measure.</p>
<p>If the tart pan is larger than called for in the recipe, the tart will cook more
                    quickly, as the tart is shallower. A smaller tart pan should be filled
                    carefully, leaving at least 1/4 inch at the top of the crust. Pour excess
                    filling into oiled ramekins and bake as a crustless pie. </p>
</div>
<h2><a id="d0e1935" xmlns:saxon="http://icl.com/saxon"></a>Roasted Tomato Tart </h2>
<p>
<span class="emphasis">
<em>Makes 1 (8- or 9-inch) tart </em>
</span>
</p>
<p>The cheese should be as good as you can get—fresh mozzarella, goat cheese, or
                Gruyère, for instance. Serve at room temperature as a snack or appetizer.
                Alternately, treat this as an upscale grilled cheese and tomato sandwich and serve
                it with tomato soup on a rainy day. Not all tarts are round. Bake oblong, either
                free-form or in a tart pan, to cut easily into bite-size or larger pieces. Round
                tarts are usually cut into wedges. Alert: The amount of tomatoes will vary depending
                on the size of the tart. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1 recipe (8- or 9-inch) <span class="link" href="#/ch13#piecrust" title="Link target outside of Sample">piecrust</span></td>
</tr>
<tr>
<td>1-1/2 cups grated or sliced fresh mozzarella, soft goat cheese, or other
                        cheese, divided </td>
</tr>
<tr>
<td>1 recipe <span class="link" href="#/ch07b#roastedtomatoes" title="Link target outside of Sample">Oven-Roasted Tomatoes</span>
</td>
</tr>
<tr>
<td>2 to 3 tablespoons chopped fresh basil, thyme, oregano, or marjoram,
                        optional </td>
</tr>
</tbody></table>
<p>Prebake the piecrust in a tart pan or free-form and set aside to cool. </p>
<p>Preheat oven to 375 degrees. </p>
<p>Layer the bottom of the piecrust with the cheese, reserving 1/4 cup for the top. Add
                the tomatoes, spreading evenly over the cheese. Move to a rimmed baking sheet. </p>
<p>Bake 15 to 20 minutes, until cheese is melted. Sprinkle with remaining cheese and
                return to oven for a few minutes. Add herbs if desired. Allow to rest 5 minutes
                before cutting. </p>
<p><span class="bold"><strong>Variation:</strong></span> Substitute roasted grape or
                cherry <span class="link" href="#/ch07b#roastedtomatoes" title="Link target outside of Sample">tomatoes</span>. </p>
<div class="mediaobject" title="Photo of the recipe or step above.">
<img alt="Photo of the recipe or step above." src="images/RCM_0450.jpg"/>
</div>
<div class="sidebar">
<p class="title">
<b></b>
</p>
<p>Once we had extra <span class="link" href="#/ch13#creamcheese" title="Link target outside of Sample">Very Versatile Cream Cheese
                        Dough</span> left over, some roasted tomatoes, and a handful of lightly cooked
                    fresh spinach leaves. We threw it all together with some caramelized onions and
                    goat cheese and baked as above. I swoon still with the memory of it. </p>
</div>
<h2><a id="d0e1984" xmlns:saxon="http://icl.com/saxon"></a>Nikki’s Roasted Tomato and
                Zucchini Lattice Tart </h2>
<p>
<span class="emphasis">
<em>Makes 1 (8- or 9-inch) tart </em>
</span>
</p>
<p>To call this a “knock their eyes out” pie would be fair. It’s stunning. My intern
                Nikki Moore developed this recipe one summer from some ideas we batted around as we
                stared at a near-empty refrigerator. It is remarkably easy. Don’t feel constrained
                to do exactly as we did—make another design. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1 recipe (8- or 9-inch) <span class="link" href="#/ch13#piecrust" title="Link target outside of Sample">piecrust</span></td>
</tr>
<tr>
<td>1 cup soft goat cheese, divided </td>
</tr>
<tr>
<td>8 to 10 slices <span class="link" href="#/ch07b#roastedtomatoes" title="Link target outside of Sample">Oven-Roasted Tomatoes</span>
</td>
</tr>
<tr>
<td>1 medium zucchini </td>
</tr>
<tr>
<td>2 tablespoons oil, cook’s preference </td>
</tr>
<tr>
<td>1 to 2 tablespoons chopped fresh herbs, such as thyme or basil, optional
                    </td>
</tr>
</tbody></table>
<p>Prebake the piecrust in an 8- or 9-inch tart pan and set aside to cool. If using a
                tart pan with a removable bottom, surround the bottom of the pan with foil to
                prevent the filling from possibly leaking out while baking. </p>
<p>Preheat oven to 375 degrees. </p>
<p>Layer the prebaked crust with the cheese, saving 1/2 cup for the top. Add the tomato
                slices, spreading evenly over the cheese. Sprinkle with the remaining cheese on top. </p>
<p>Meanwhile, slice the zucchini vertically into long strips with a vegetable peeler.
                Heat the oil in a small frying pan, add the zucchini, and wilt just a few minutes.
                Cool sufficiently to handle and form into a lattice on the top of the cheese. Make a
                horizontal row of zucchini, leaving space between the rows. Weave the remaining
                zucchini under and over the horizontal row, leaving a space again so the tomatoes
                and cheese show through. Moved to a rimmed baking sheet. </p>
<p>Bake 10 to 15 minutes, or until cheese is melted. Add herbs if desired. Allow to rest
                5 minutes before cutting. </p>
<p>
<span class="bold">
<strong>Variations:</strong>
</span>
</p>
<div class="itemizedlist">
<ul class="itemizedlist">
<li class="listitem">
<p>If no tart pan is to be had, roll the pie dough into a large piece and
                            cut, using a guide or freehand, into a circle or square. Move to an
                            oiled or parchment-lined baking sheet. Partially prebake. Fill as above.
                        </p>
</li>
<li class="listitem">
<p>Omit the zucchini and substitute 1-1/2 cups grated or sliced mozzarella
                            cheese for the goat cheese. </p>
</li>
</ul>
</div>
<h2><a id="d0e2033" xmlns:saxon="http://icl.com/saxon"></a>Crab Tart </h2>
<p>
<span class="emphasis">
<em>Makes 1 (9 x 12-inch) tart </em>
</span>
</p>
<p>Seen frequently at bridal showers, seafood and custard dishes show up on Southern
                tables regularly because they stretch the expensive crabmeat while presenting a
                beautiful, rich, and tasty dish. The piecrust supports the filling as it is cut into
                squares and served hot or cold. Lump crab is not necessary in this recipe and would
                be a waste. Remember, the crab is already cooked. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1 double recipe <span class="link" href="#/ch13#doublepiecrust" title="Link target outside of Sample">piecrust</span>
</td>
</tr>
<tr>
<td>2 tablespoons butter </td>
</tr>
<tr>
<td>1 cup chopped green onions or scallions, both white and green parts </td>
</tr>
<tr>
<td>2/3 cup vermouth or dry Madeira </td>
</tr>
<tr>
<td>1 pound backfin crabmeat, picked over for shells </td>
</tr>
<tr>
<td>1 pound claw crabmeat, picked over for shells </td>
</tr>
<tr>
<td>10 large eggs </td>
</tr>
<tr>
<td>1 tablespoon salt</td>
</tr>
<tr>
<td>1 teaspoon freshly ground black pepper </td>
</tr>
<tr>
<td>1/4 cup tomato paste </td>
</tr>
<tr>
<td>4 cups heavy cream </td>
</tr>
<tr>
<td>3/4 cup grated Gruyère cheese </td>
</tr>
<tr>
<td>2 tablespoons chopped fresh thyme, oregano, and/or fennel frond </td>
</tr>
</tbody></table>
<p>Preheat oven to 425 degrees. </p>
<p>Roll out and line a 9 x 13-inch rimmed baking sheet with the piecrust and prebake.
                Set aside to cool. </p>
<p>While the crust is cooling, melt the butter in a frying pan. Add the green onions and
                cook briefly, 2 or 3 minutes. Stir in the vermouth or Madeira. Toss the crabmeat in
                and sauté for 2 to 3 minutes, until the liquid appears to have evaporated. </p>
<p>Beat together the eggs, salt, pepper, and tomato paste in another bowl until the
                tomato paste is thoroughly incorporated. Add the crab mixture and the cream. Spoon
                into the prebaked crust, taking care to distribute the entire mixture evenly
                throughout the pan. </p>
<p>Toss the cheese with the herbs and sprinkle over the tart, stirring gently to
                incorporate a little of the cheese in with the crab mixture. Reduce oven to 350
                degrees. Bake about 45 to 50 minutes, until the tart is firm around the edges and
                fairly firm in the center. The center will firm up slightly as it cools. Cut into
                squares when the tart has cooled somewhat and the center has set. Serve hot or cold.
                This tart can be refrigerated and reheated in a 300-degree oven.</p>
<p>
<span class="bold">
<strong>Variation: Savannah Crab Custard Tart </strong>
</span>
</p>
<p>This more delicate and traditional tart omits the green onion, tomato paste, and
                cheese. It changes the ratio to accommodate a 9-inch tart pan or pie plate, which
                should be prebaked. Mix 2 tablespoons melted butter, 1 cup milk, 1 cup heavy cream,
                and 4 eggs. Add the salt, pepper, crabmeat, and vermouth or Madeira and mix. Spoon
                evenly into the prebaked crust and bake 30 to 40 minutes, until the custard is set. </p>
<p><span class="bold"><strong>Variation:</strong></span>Chopped cooked shrimp is a good
                substitute for all or part of the crab.</p>
<div class="sidebar">
<p class="title">
<b></b>
</p>
<h4><a id="d0e2094" xmlns:saxon="http://icl.com/saxon"></a>Soggy or Cracked
                    Piecrusts </h4>
<p>Sprinkling breadcrumbs into a prebaked crust helps keep it from becoming soggy.
                    Another method is to brush mustard on the inside of the piecrust before filling
                    and baking. If a piecrust cracks while prebaking, brush with beaten egg yolk, or
                    sprinkle the bottom lightly with grated cheese before adding filling. </p>
</div>
<h2><a id="d0e2098" xmlns:saxon="http://icl.com/saxon"></a>Shrimp and Okra Pie </h2>
<p>
<span class="emphasis">
<em>Makes 1 (9-inch) pie </em>
</span>
</p>
<p>This medley makes a pie fit for a queen, a lunch, or a starter. It fits the bill with
                beautiful colors and flavors. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1 recipe (9-inch) <span class="link" href="#/ch13#piecrust" title="Link target outside of Sample">piecrust</span>
</td>
</tr>
<tr>
<td>1 pound fresh okra, sliced 1/4 inch thick </td>
</tr>
<tr>
<td>1 medium onion, chopped </td>
</tr>
<tr>
<td>1/2 red or other bell pepper, chopped </td>
</tr>
<tr>
<td>3 garlic cloves, chopped </td>
</tr>
<tr>
<td>1/4 cup oil, cook’s preference </td>
</tr>
<tr>
<td>1 tablespoon vinegar </td>
</tr>
<tr>
<td>1/3 cup sliced green onions</td>
</tr>
<tr>
<td>3/4 pound raw medium shrimp, peeled and deveined </td>
</tr>
<tr>
<td>3/4 cup breadcrumbs, divided </td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>Freshly ground black pepper </td>
</tr>
</tbody></table>
<p>Prebake the piecrust in an 8- or 9-inch pie or tart pan and set aside to cool. </p>
<p>Preheat oven to 375 degrees. </p>
<p>Stir okra, onion, bell pepper, garlic, and oil together in a large Dutch oven. Bake
                35 to 40 minutes, stirring occasionally. Add vinegar and stir; continue to bake 10
                to 15 minutes more, stirring occasionally. Remove from oven to continue cooking on
                the stovetop over medium-high heat. Add green onions and shrimp, and cook 3 minutes,
                until the shrimp are pink. Remove from heat and add 1/2 cup of the breadcrumbs.
                Season to taste with salt and pepper. Fill prebaked piecrust with cooked okra and
                shrimp mixture. Sprinkle with remaining breadcrumbs. Move to a rimmed baking sheet.
                Bake 5 to 7 minutes. Serve hot.</p>
<h2><a id="d0e2141" xmlns:saxon="http://icl.com/saxon"></a>Pepper Jelly Turnovers </h2>
<p>
<span class="emphasis">
<em>Makes 2 dozen </em>
</span>
</p>
<p>This golden cheese pastry takes no time to pull together in a food processor or by
                hand. Filled with hot pepper jelly, these bite-sized jewels sparkle in the mouth. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>5 ounces sharp Cheddar cheese, cut into 1/2-inch cubes </td>
</tr>
<tr>
<td>1 cup all-purpose flour </td>
</tr>
<tr>
<td>1/2 cup butter, cut into 1/2-inch cubes </td>
</tr>
<tr>
<td>3 to 4 tablespoons ice water </td>
</tr>
<tr>
<td>1/2 cup or 1 (4-ounce) jar <span class="link" href="#/ch16#pepperjelly" title="Link target outside of Sample">Hot Pepper Jelly</span></td>
</tr>
<tr>
<td>2 to 3 tablespoons chopped smoked or country ham, or turkey ham, or turkey,
                        optional </td>
</tr>
</tbody></table>
<p>Preheat oven to 375 degrees. </p>
<p>Pulse the cheese and flour together just briefly in the bowl of a food processor
                fitted with the metal blade. Add the butter and process or blend until it is about
                the size of garden peas. Add the ice water and process briefly until the dough
                starts to come together. </p>
<p>Turn the dough out onto a board and shape it into a flat round. Roll it out (between
                sheets of waxed paper if necessary) 1/8 inch thick and cut into rounds with a 2-inch
                biscuit cutter. Place approximately 1/3 teaspoon Hot Pepper Jelly and 1/4 teaspoon
                chopped ham in the center of each round and fold over. Crimp the edges well with a
                fork to seal or the jelly will run out. </p>
<p>Move the turnovers to a rimmed baking sheet and bake 10 to 15 minutes. The turnovers
                can be frozen before or after baking. Defrost and bake or reheat before serving. </p>
<h2><a id="d0e2173" xmlns:saxon="http://icl.com/saxon"></a>Cheese Sausage Pinwheels </h2>
<p>
<span class="emphasis">
<em>Makes 24 pinwheels </em>
</span>
</p>
<p>Combine the taste of cheese with sausage or ham, roll up and bake, and what do you
                have? Something pretty, oh-so-good, and easy to eat. These freeze well, too. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1 recipe food processor <span class="link" href="#/ch12#funvariation" title="Link target outside of Sample">golden cheese biscuits</span></td>
</tr>
<tr>
<td>1 (16-ounce) roll mild or hot sausage, lightly cooked, drained and cooled
                    </td>
</tr>
</tbody></table>
<p>Preheat oven to 400 degrees. </p>
<p>Pat the dough on a floured surface into a 14 x 10-inch rectangle, about 1/4 inch
                thick. Crumble the lightly cooked sausage evenly over the biscuit dough. </p>
<p>Starting on a long side of the rectangle, use the heel of one hand and roll the dough
                up in jelly-roll style. Slice the roll into 1/2-inch-wide pinwheels. Move to an
                oiled, rimmed baking sheet, leaving space on the baking sheet for biscuits to
                spread, and bake 15 to 18 minutes, until lightly browned. </p>
<p>
<span class="bold">
<strong>Variation: Cheese Pinwheels </strong>
</span>
</p>
<p>Roll out biscuit dough to 1/4-inch thickness. Spread generously with grated cheese,
                sprinkle with hot red pepper, and roll up like a jelly roll. Slice the roll into
                1/2-inch-wide pinwheels; move to an oiled baking sheet, and bake in a moderate oven
                until lightly browned. They are good hot or cold. </p>
<p>
<span class="bold">
<strong>Variation: Ham Pinwheels </strong>
</span>
</p>
<p>Combine 3/4 pound chopped and cooked Virginia country ham, 1 chopped garlic clove, 1
                chopped medium onion, 1/2 cup melted butter, 1/2 cup mayonnaise, 2/3 cup peanut
                butter, and a dash of hot sauce if desired. Follow directions above, substituting
                this mixture for the cooked sausage. </p>
<h2><a id="d0e2209" xmlns:saxon="http://icl.com/saxon"></a>Hot Country Ham-and-Cheese
                Rolls with Dijon–Poppy Seed Sauce </h2>
<p>
<span class="emphasis">
<em>Makes 48 small sandwiches </em>
</span>
</p>
<p>These warm little rolls will be the hit of any party. Thinly sliced pears or apples
                add a little extra sweetness, although figs would also be a good addition. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1 cup mayonnaise </td>
</tr>
<tr>
<td>4 tablespoons Dijon mustard </td>
</tr>
<tr>
<td>1 large onion, finely minced </td>
</tr>
<tr>
<td>3 tablespoons poppy seeds </td>
</tr>
<tr>
<td>2 tablespoons Worcestershire sauce </td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>Freshly ground black pepper </td>
</tr>
<tr>
<td>1 recipe <span class="link" href="#/ch12#processor" title="Link target outside of Sample">Food Processor Rolls
                            dough</span> baked into 48 small rolls </td>
</tr>
<tr>
<td>2 pounds sliced Gruyère cheese </td>
</tr>
<tr>
<td>2 pounds country ham or baked ham, thinly sliced </td>
</tr>
<tr>
<td>4 apples or pears, thinly sliced </td>
</tr>
</tbody></table>
<p>Preheat oven to 350 degrees. </p>
<p>Blend together mayonnaise, mustard, onion, poppy seeds, and Worcestershire sauce;
                season to taste with salt and pepper. Spread sauce thinly on the insides of both
                sides of the rolls, reserving leftover sauce. </p>
<p>Layer cheese, ham, fruit, and another layer of cheese on rolls, beginning and ending
                with cheese, which holds the rolls together when melted. This can be done ahead of
                time and refrigerated up to several hours. Spread or brush reserved sauce on top of
                rolls before baking. Wrap with foil and bake for 20 minutes, or until heated
                through. </p>
<p><span class="bold"><strong>Variation:</strong></span> Substitute 12 to 14 sliced figs
                for the apples or pears.</p>
<h2><a id="brittlebread" xmlns:saxon="http://icl.com/saxon"></a>Sour Cream Brittlebread </h2>
<p>
<span class="emphasis">
<em>Makes 40 (1-1/2-inch) crackers </em>
</span>
</p>
<p>Crackers, brittlebread, and other thin, crispy “platforms” are one of the miracles of
                Southern cooking, easily made in the food processor. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>2-3/4 cups all-purpose flour </td>
</tr>
<tr>
<td>1/4 cup granulated sugar </td>
</tr>
<tr>
<td>1/2 teaspoon salt, optional </td>
</tr>
<tr>
<td>1/2 teaspoon baking soda </td>
</tr>
<tr>
<td>1/2 cup butter, cut into 1/4-inch cubes </td>
</tr>
<tr>
<td>1 cup sour cream </td>
</tr>
<tr>
<td>1 to 3 teaspoons freshly ground black pepper, optional </td>
</tr>
<tr>
<td>1 to 2 tablespoons kosher salt </td>
</tr>
</tbody></table>
<p>Preheat oven to 400 degrees. </p>
<p>Pulse the flour, sugar, salt, and baking soda together in the bowl of a food
                processor fitted with a metal blade. Add butter and pulse several times. Add sour
                cream and pepper, and pulse to a soft but not sticky dough. </p>
<p>Divide in half and roll out each half to 10 x 18 inches, paper-thin, on a floured
                board or between 2 pieces of plastic wrap. Use a pizza wheel or knife and cut into
                3-inch squares. Move to ungreased 11 x 17-inch jelly-roll pans or rimmed baking
                sheets. Cut again into 1-1/2-inch pieces. Sprinkle with kosher salt. Bake 5 to 8
                minutes. Turn off the heat and allow the bread to crisp in the oven as long as
                necessary. Re-crisp in the oven after storing, as our humidity is the foe of
                crispness.</p>
<div class="sidebar">
<p class="title">
<b></b>
</p>
<h4><a id="d0e2287" xmlns:saxon="http://icl.com/saxon"></a>Crackers, No. 1 </h4>
<p>Rub six ounces of butter into two pounds sifted flour; dissolve a teaspoonful
                    (level full) of soda in a wineglass of buttermilk; strain this through a fine
                    sieve to the flour; add a teaspoonful of salt; beat well; roll thin; bake. If
                    not crisp when first baked, put them again into a slack oven, and merely heat
                        over.—<span class="emphasis"><em>Mrs. Hill’s New Cook Book,</em></span> 1872
                </p>
</div>
<h2><a id="straws" xmlns:saxon="http://icl.com/saxon"></a>Cheese Straws </h2>
<p>
<span class="emphasis">
<em>Makes 100 </em>
</span>
</p>
<p>Southern cheese straws are made with a short crust and cheese dough. Hand-grated
                cheese is preferred over packaged pre-shredded cheese. If using a cookie press,
                grate the cheese very finely so it won’t clog the press. Make sure the butter is at
                room temperature but not melted, so it is soft enough to ensure that the cheese and
                butter mixture will be smooth and not gritty. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1 cup butter, room temperature </td>
</tr>
<tr>
<td>8 ounces Parmesan cheese, finely grated </td>
</tr>
<tr>
<td>8 ounces sharp Cheddar or Gruyère cheese, finely grated </td>
</tr>
<tr>
<td>3/4 teaspoon Dijon mustard </td>
</tr>
<tr>
<td>2-1/3 cups all-purpose flour </td>
</tr>
<tr>
<td>1/4 teaspoon salt </td>
</tr>
<tr>
<td>1 teaspoon baking powder </td>
</tr>
<tr>
<td>1/4 to 1/2 teaspoon ground hot red pepper </td>
</tr>
</tbody></table>
<p>Preheat oven to 375 degrees. </p>
<p>Cut the butter into thin pieces, approximately tablespoon-size, and beat with an
                electric hand mixer in a very large mixing bowl until butter is soft. Add all the
                cheese and beat with the butter until the mixture is smooth. Stir in the mustard. </p>
<p>Whisk together the flour, salt, baking powder, and hot red pepper. Add to the butter
                mixture and beat until combined. Form into a ball. </p>
<p>Press dough out with a cookie press into straws, following manufacturer’s
                instructions, or use the following procedure: Divide dough into fourths. On waxed
                paper, roll each piece into a rectangle 1/3 inch thick. Use a pastry wheel to cut
                dough into 4- x 1/2-inch strips. Move straws onto a rimmed baking sheet lined with a
                silicone mat or parchment paper. Bake at 375 degrees for 8 to 15 minutes, until
                light brown. Watch carefully, as overly browned straws aren’t tasty. Cool on a rack. </p>
<p>Every oven is different. Cook the first batch, watching carefully, and if too brown,
                reduce heat to 325 degrees. Rotate pan halfway through baking time. Use more than
                one baking sheet at a time only if the sheets may be placed in the oven without
                overlapping. Air must freely circulate or the bottoms will be burned and the tops
                unbrowned. Switch shelves and positions of the pans as necessary to prevent top or
                bottom from browning. Take care: white cheese will not brown as much as yellow. </p>
<p>Once the aroma comes wafting out of the oven, they are usually done. Cooking further
                may burn the cheese. Remove with a metal spatula to cool on a rack. These keep
                several days at room temperature in a tightly sealed container, or frozen up to 3
                months. Due to the heavy humidity in the South, it may be necessary to re-crisp
                straws on a baking sheet in a 300-degree oven. </p>
<p><span class="bold"><strong>Variation:</strong></span> Use cookie cutters, a pastry
                bag, or an indented pizza slicer to shape cheese straws. Cut into coins, triangles,
                hearts, or specialty shapes for special parties. </p>
<div class="mediaobject" title="Photo of the recipe or step above.">
<img alt="Photo of the recipe or step above." src="images/_IIP5886.jpg"/>
<img alt="Photo of the recipe or step above." src="images/_IIP5831.jpg"/>
</div>
<h2><a id="minimuffins" xmlns:saxon="http://icl.com/saxon"></a>Cornbread Mini-Muffins </h2>
<p>
<span class="emphasis">
<em>Makes 5 dozen </em>
</span>
</p>
<p>These are like crostini—anything can be put on top of them. For a hot appetizer, top
                with shredded barbecue pork. For a room-temperature appetizer, top with pimento
                cheese, country ham, or any savory jam or chutney. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>2 cups self-rising white cornmeal mix </td>
</tr>
<tr>
<td>2 cups buttermilk </td>
</tr>
<tr>
<td>1/2 cup all-purpose flour </td>
</tr>
<tr>
<td>2 large eggs </td>
</tr>
<tr>
<td>1/4 cup butter, melted </td>
</tr>
<tr>
<td>2 tablespoons granulated sugar </td>
</tr>
</tbody></table>
<p>Preheat oven to 400 degrees. Oil mini-muffin tins and set aside. </p>
<p>Stir together all ingredients in a medium bowl until the cornmeal mix is moistened.
                Spoon 1 tablespoon of batter into each muffin cup. </p>
<p>Move muffin tins to oven and bake 15 minutes, or until the muffins tops are lightly
                brown. Cool on a wire rack. Serve warm or at room temperature. Store in the freezer,
                tightly wrapped, for up to 1 month. </p>
<p>To reheat, remove from freezer and bake at 350 degrees for 5 minutes. </p>
<div class="sidebar">
<p class="title">
<b></b>
</p>
<p>If the batter tends to stick to the spoon, wipe off excess and oil the spoon to
                    prevent batter build-up. </p>
</div>
<h2><a id="gritscakes" xmlns:saxon="http://icl.com/saxon"></a>Grits Cakes </h2>
<p>
<span class="emphasis">
<em>Makes 8 cakes </em>
</span>
</p>
<p>Grits cakes can be served hot or cold, presented on a beautiful plate for a sit-down
                starter or main course, or cut bite-sized and served as a stand-up snack. The
                variations are endless. Change herbs, top with shrimp, bacon, and tomato—whatever
                looks lively and tastes phenomenal. The secret is never to throw away cooked grits
                but to repurpose them. They can be refrigerated or frozen. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>4 cups cooked grits </td>
</tr>
<tr>
<td>1 tablespoon chopped garlic </td>
</tr>
<tr>
<td>1/2 cup grated extra-sharp Cheddar cheese </td>
</tr>
<tr>
<td>2 tablespoons chopped fresh chives </td>
</tr>
<tr>
<td>4 tablespoons butter, room temperature, divided </td>
</tr>
<tr>
<td>Hot pepper sauce to taste </td>
</tr>
</tbody></table>
<p>Butter a 9 x 5 x 2-1/2-inch loaf pan and line it with parchment paper or aluminum
                foil. Stir hot grits, garlic, cheese, chives, and 2 tablespoons of the butter
                together. Season to taste with hot sauce. Pour the grits into the pan and
                refrigerate until chilled and firm. </p>
<p>When ready to serve, unmold the grits and slice them into 8 squares or cut with
                cookie cutters into shapes. Heat the remaining butter in a heavy-bottomed nonstick
                frying pan over medium-high heat. Cook the grits cakes for 5 minutes on each side,
                or until golden and heated through. </p>
<p>These can be frozen before or after cooking. </p>
<p><span class="bold"><strong>Variation:</strong></span> Cut into tiny bite-sized
                circles.</p>
<div class="sidebar">
<p class="title">
<b></b>
</p>
<h4><a id="d0e2412" xmlns:saxon="http://icl.com/saxon"></a>Shaping fun with Cooked
                    Grits </h4>
<p>Cooked grits can be molded into small round cakes, hearts, pyramids, and other
                    shapes. Spread out cooked grits on a rimmed baking sheet around 1/2 inch thick
                    or as desired. Cool briefly or up to several days in the refrigerator, covered.
                    Cut out with cookie cutters or just with a knife into squares or oblongs. Serve
                    as is, or top with little tomatoes, shrimp, or crab. Another way is to heap them
                    in a loaf pan or other mold. When cool, turn out and slice or shape. A trick is
                    to shape into a cylinder like store-bought cookie dough, wrap with plastic wrap,
                    and roll again to smooth. Chill. When ready to use, unwrap and slice. </p>
</div>
<h2><a id="d0e2416" xmlns:saxon="http://icl.com/saxon"></a>Okra Griddle Cakes </h2>
<p>
<span class="emphasis">
<em>Makes 16 cakes </em>
</span>
</p>
<p>Flavors dance in the mouth in these batter cakes. Wait until everyone is gathered
                around before starting to cook them, and raves will be in store. This is an ideal
                starter for when guests’ arrival time is uncertain, or when a kitchen gathering is
                the order of the day. Have everything ready ahead of time except the cakes.
                Otherwise, a stack of the still warm cakes on the buffet table will entice those who
                want a dollop of sour cream. These are just enough to whet the appetite, a cross
                between fried okra and cornbread. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>4 slices cooked bacon, drippings reserved </td>
</tr>
<tr>
<td>1 cup cooked okra, finely chopped </td>
</tr>
<tr>
<td>1-1/2 cups self-rising cornmeal mix </td>
</tr>
<tr>
<td>1/2 cup all-purpose flour </td>
</tr>
<tr>
<td>1 tablespoon granulated sugar </td>
</tr>
<tr>
<td>1-2/3 cups buttermilk </td>
</tr>
<tr>
<td>3 tablespoons butter, melted </td>
</tr>
<tr>
<td>2 large eggs, lightly beaten </td>
</tr>
<tr>
<td>Shortening or vegetable oil for frying </td>
</tr>
<tr>
<td>Sour cream, optional </td>
</tr>
</tbody></table>
<p>Finely chop the cooked bacon and okra. </p>
<p>Up to 2 hours before serving, whisk together cornmeal mix, flour, and sugar in a
                bowl. Stir in buttermilk, butter, and eggs until just mixed. Add finely chopped
                bacon and okra to batter. </p>
<p>Heat a griddle or large iron skillet until hot. Add enough oil to coat the bottom.
                Sprinkle in a bit of batter to test that the pan is hot enough to sizzle and that
                the batter is a pourable consistency. Add water to thin if necessary. Ladle 1/4 cup
                batter for each griddle cake onto hot griddle and cook until the top of the pancake
                is dotted with large bubbles and the bottom is light brown. Turn with a large
                spatula, and cook until the other side is lightly browned. Keep warm in a 200-degree
                oven on a rack over a baking sheet or serve immediately. Continue with the rest of
                the batter until all is gone. </p>
<p>Serve hot with optional sour cream. </p>
<p>
<span class="bold">
<strong>Variations:</strong>
</span>
</p>
<div class="itemizedlist">
<ul class="itemizedlist">
<li class="listitem">
<p>Top the sour cream with a little chopped bacon and okra. </p>
</li>
<li class="listitem">
<p>Substitute a little chopped turnip greens and hot pepper, a few chopped
                            shrimp or crab in the batter for the okra and bacon. </p>
</li>
</ul>
</div>
<div class="sidebar">
<p class="title">
<b></b>
</p>
<p>Griddle cakes can be made thick or thin, coin- or pancake-sized. What’s necessary
                    is a hot pan—and a watchful eye. The batter must be hot enough to rise in
                    bubbles to the top of the pancake, indicating it is ready to turn, or a goopy
                    mess will result. </p>
</div>
<h2><a id="caviar" xmlns:saxon="http://icl.com/saxon"></a>Mississippi Caviar </h2>
<p>
<span class="emphasis">
<em>Serves 25 </em>
</span>
</p>
<p>This wonderful make-ahead dip, made from black-eyed peas—the South’s caviar—can also
                be a vegetable bed for grilled or smoked chops. Cover and refrigerate for up to 4 or
                5 days or freeze up to 3 months. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>3 (16-ounce) cans black-eyed peas, drained </td>
</tr>
<tr>
<td>1/2 cup finely chopped green bell pepper </td>
</tr>
<tr>
<td>1/2 cup finely chopped red bell pepper </td>
</tr>
<tr>
<td>3/4 cup finely chopped hot peppers </td>
</tr>
<tr>
<td>3/4 cup finely chopped onion </td>
</tr>
<tr>
<td>1/4 cup drained and finely chopped pimento </td>
</tr>
<tr>
<td>1 garlic clove, chopped </td>
</tr>
<tr>
<td>1/3 cup red wine vinegar </td>
</tr>
<tr>
<td>2/3 cup olive oil </td>
</tr>
<tr>
<td>1 tablespoon Dijon mustard </td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>Tabasco sauce </td>
</tr>
</tbody></table>
<p>In a large mixing bowl, combine the peas, bell pepper, hot peppers, onion, pimento,
                and garlic. In a separate bowl, whisk together the vinegar, oil, and mustard, and
                pour over the bean mixture; mix well. Season to taste with salt and Tabasco. With a
                wooden spoon or potato masher, mash the bean mixture slightly. Refrigerate until
                ready to serve. Drain the caviar well before serving. </p>
<h2><a id="d0e2505" xmlns:saxon="http://icl.com/saxon"></a>Southern Hummus </h2>
<p>
<span class="emphasis">
<em>Makes 6 cups </em>
</span>
</p>
<p>Sold fresh, frozen, and canned, butter beans are often found in the Southern larder,
                and New Southern chefs have taken to creating “Southern hummus.” Any form can be
                used in this recipe, with the caveat that the canned beans and frozen beans will
                require less cooking than fresh and should be drained before using. Seasoning will
                always vary according to the bean, so the measurements are just a guideline. This
                dip is significantly better the next day. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>2 pounds fresh or frozen butter beans, butter peas, or English peas </td>
</tr>
<tr>
<td>2 medium Vidalia or other onions, chopped </td>
</tr>
<tr>
<td>8 garlic cloves, smashed and peeled </td>
</tr>
<tr>
<td>1 tablespoon salt </td>
</tr>
<tr>
<td>1/4 cup chopped fresh cilantro </td>
</tr>
<tr>
<td>1/4 cup smooth peanut butter or tahini </td>
</tr>
<tr>
<td>2 teaspoons ground cumin seed </td>
</tr>
<tr>
<td>1/4 teaspoon ground hot red pepper </td>
</tr>
<tr>
<td>3 ounces fresh lemon juice </td>
</tr>
<tr>
<td>1/2 cup olive oil, divided </td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>Freshly ground black pepper </td>
</tr>
<tr>
<td>Ground coriander seed </td>
</tr>
<tr>
<td>Quartered pita bread, other sturdy bread, or raw vegetables for dipping
                    </td>
</tr>
</tbody></table>
<p>Add the beans, onion, garlic, and salt to boiling water to cover in a large pot.
                Reduce heat to a simmer, cover, and simmer about 10 to 20 minutes, or until beans
                are tender; <span class="link" href="#/ch06#skim" title="Link target outside of Sample">skim</span> off any foam as needed.
                Drain the skimmed bean mixture and move to a food processor bowl or strong blender.
                Add the cilantro, peanut butter, cumin, hot red pepper, lemon juice, and 1/3 cup of
                oil. Purée until smooth. Season to taste with salt and pepper. This can be made
                several days in advance and refrigerated. Drizzle remaining oil on top just before
                serving, and sprinkle with coriander. </p>
<p>Serve with pita bread, raw vegetables, <a class="link" href="#/ch04#brittlebread" title="Sour Cream Brittlebread">Sour Cream Brittlebread</a>, <a class="link" href="#/ch04#minimuffins" title="Cornbread Mini-Muffins">Cornbread
                    Mini-Muffins</a>, or other crackers. </p>
<p>
<span class="bold">
<strong>Variations:</strong>
</span>
</p>
<div class="itemizedlist">
<ul class="itemizedlist">
<li class="listitem">
<p>Add 1 teaspoon chopped fresh mint or dill. </p>
</li>
<li class="listitem">
<p>Substitute white acre peas, black-eyed peas, or peanuts for the butter
                            beans.</p>
</li>
</ul>
</div>
<h2><a id="d0e2568" xmlns:saxon="http://icl.com/saxon"></a>Throw-Together Corn and Field
                Pea Dip </h2>
<p>
<span class="emphasis">
<em>Makes 4 cups </em>
</span>
</p>
<p>Unique and very easy, this can be thrown together the night before the party or even
                an hour before to let the flavors meld. Fresh raw (green) corn, cooked corn, and
                canned kernels all work fine in this recipe. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>2 cups cooked field peas with snaps, or 1 (15-ounce) can, rinsed and drained
                    </td>
</tr>
<tr>
<td>1-1/2 cups cooked corn or 1 (11-ounce) can white shoepeg corn, drained </td>
</tr>
<tr>
<td>1/2 cup peeled, seeded, and diced tomatoes </td>
</tr>
<tr>
<td>1 jalapeño pepper or other small hot pepper, seeded and finely chopped </td>
</tr>
<tr>
<td>1/4 cup finely chopped onion </td>
</tr>
<tr>
<td>1 garlic clove, chopped </td>
</tr>
<tr>
<td>2 tablespoons chopped fresh parsley </td>
</tr>
<tr>
<td>1/4 cup olive oil </td>
</tr>
<tr>
<td>1/4 cup red wine vinegar </td>
</tr>
<tr>
<td>2 tablespoons fresh lemon juice </td>
</tr>
<tr>
<td>Salt </td>
</tr>
</tbody></table>
<p>Toss the peas, corn, tomatoes, pepper, onion, garlic, and parsley in a bowl. </p>
<p>Whisk together oil, vinegar, and lemon juice. Pour over pea mixture and toss. Season
                to taste with salt. Cover and refrigerate at least 1 hour or overnight. </p>
<p>Drain before serving with <a class="link" href="#/ch04#brittlebread" title="Sour Cream Brittlebread">Sour Cream Brittlebread</a>, tortilla chips, or
                crackers. </p>
<h2><a id="d0e2609" xmlns:saxon="http://icl.com/saxon"></a>Food Processor Black-Eyed Pea
                Dip </h2>
<p>
<span class="emphasis">
<em>Makes 1-1/2 cups </em>
</span>
</p>
<p>The food processor makes this a 5-minute dish if the peas are already cooked. Serve
                with crackers or tortilla chips. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1/4 cup fresh parsley </td>
</tr>
<tr>
<td>1 teaspoon fresh thyme </td>
</tr>
<tr>
<td>2 garlic cloves, peeled</td>
</tr>
<tr>
<td>2 cups cooked black-eyed peas or 1 (15-ounce) can black-eyed peas, rinsed
                        and drained, or other cooked field pea </td>
</tr>
<tr>
<td>3 tablespoons olive oil, divided </td>
</tr>
<tr>
<td>1 tablespoon fresh lemon juice </td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>Freshly ground black pepper </td>
</tr>
</tbody></table>
<p>Chop the herbs and garlic in a food processor fitted with a metal blade. Add peas, 2
                tablespoons of the oil, and lemon juice, and process until smooth. Season to taste
                with salt and pepper. </p>
<p>Move to a bowl and drizzle remaining oil on top. Serve with <a class="link" href="#/ch04#brittlebread" title="Sour Cream Brittlebread">Sour Cream
                    Brittlebread</a>, <a class="link" href="#/ch04#minimuffins" title="Cornbread Mini-Muffins">Cornbread Mini-Muffins</a>, or other
                crackers.</p>
<h2><a id="chunky" xmlns:saxon="http://icl.com/saxon"></a>Chunky Eggplant Spread </h2>
<p>
<span class="emphasis">
<em>Makes 6 cups </em>
</span>
</p>
<p>Eggplant loves the Southern climate and soil; thus we have an abundance all summer
                long. It is difficult to brown if not degorged prior to cooking, as the eggplant
                contains water. If brownness is not essential, proceed as desired. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1 large eggplant </td>
</tr>
<tr>
<td>1/2 cup oil, cook’s preference, divided </td>
</tr>
<tr>
<td>1 large onion, chopped </td>
</tr>
<tr>
<td>1/2 cup diced celery </td>
</tr>
<tr>
<td>2 cups peeled whole tomatoes or 1 (15-ounce) can, coarsely chopped </td>
</tr>
<tr>
<td>1 to 2 tablespoons tomato paste, optional </td>
</tr>
<tr>
<td>2 tablespoons red wine vinegar </td>
</tr>
<tr>
<td>1/3 cup pitted and sliced black olives </td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>Freshly ground black pepper </td>
</tr>
<tr>
<td>2 tablespoons roasted peanuts </td>
</tr>
<tr>
<td>1/2 cup raisins </td>
</tr>
</tbody></table>
<p>Cut the eggplant into 1/2-inch cubes and <span class="link" href="#/ch07#degorging" title="Link target outside of Sample">degorge</span> if desired for browning. Heat 1/4 cup oil in a
                heavy skillet. Add eggplant and cook in batches, not overcrowding the pan. Stir
                while cooking and browning; drain on paper towels. Add more oil to the pan as
                needed. </p>
<p>Add the onion and celery to the oil and cook 5 minutes, or until soft. Return the
                eggplant to the pot. Add tomatoes, tomato paste, vinegar, and olives. Season to
                taste with salt and pepper. Simmer for 30 minutes, stirring occasionally. </p>
<p>Refrigerate covered for 30 minutes or up to several days, or freeze in an airtight
                container. Remove (and defrost if necessary) to a serving dish and top with peanuts
                and raisins. </p>
<p>Serve with <a class="link" href="#/ch04#brittlebread" title="Sour Cream Brittlebread">Sour Cream Brittlebread</a>, <a class="link" href="#/ch04#minimuffins" title="Cornbread Mini-Muffins">Cornbread
                    Mini-Muffins</a>, or other crackers.</p>
<h2><a id="d0e2696" xmlns:saxon="http://icl.com/saxon"></a>Pork and Thyme Spread </h2>
<p>
<span class="emphasis">
<em>Makes 1-1/2 pints </em>
</span>
</p>
<p>Pork has a forceful personality, rich and satisfying even when made into this French
                rillette-like spread. The thyme gives an undercurrent of flavor to the spread. The
                fat is crucial for spreadability. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1-1/2 pounds pork from the back, loin, or ribs </td>
</tr>
<tr>
<td>1/4 pound pork fat from back, belly, loin, or ribs </td>
</tr>
<tr>
<td>3 garlic cloves, chopped, divided </td>
</tr>
<tr>
<td>1 tablespoon chopped fresh thyme </td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>Freshly ground black pepper </td>
</tr>
</tbody></table>
<p>Cut the pork into finger-length strips and the fat into 1/4-inch cubes. Cook the
                fatback over low heat in a large Dutch oven until it renders some fat. Add the pork
                and enough water to cover the meat, about 1 cup. Partly cover the pan and cook over
                low heat for 45 minutes without browning, stirring from time to time. Keep adding
                water as needed to prevent the pork from drying out and browning. When done, the
                meat should look white and be tender. </p>
<p>Remove meat to a cutting board, saving the liquid and fat. Pull the meat into shreds
                with two forks or shred in a food processor. </p>
<p>Move to a bowl and add most of the reserved liquid and fat to moisten and flavor. Mix
                in half the garlic and the remaining ingredients. Taste and add additional garlic as
                desired and season to taste with additional salt and pepper. </p>
<p>Spoon into pretty jars or pots without packing tightly. Cover with plastic wrap and
                refrigerate or freeze up to 3 months. Serve with small pieces of French-type bread,
                toast, crackers, or vegetables like zucchini or rolled small lettuce leaves. </p>
<p><span class="bold"><strong>Variation:</strong></span> If only minimal fresh pork fat
                is available, rinse salt pork, cut into cubes, and add as needed for softness and
                spreadability.</p>
<h2><a id="d0e2730" xmlns:saxon="http://icl.com/saxon"></a>Chicken Liver Pâté </h2>
<p>
<span class="emphasis">
<em>Serves 6 </em>
</span>
</p>
<p>It is impossible for me to think of Burge Plantation in east Georgia without thinking
                of the chicken liver pâté served there for grand summer garden parties or as a
                prelude to dinners for hungry hunters. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>4 tablespoons butter, divided </td>
</tr>
<tr>
<td>1 medium onion, finely chopped </td>
</tr>
<tr>
<td>1 garlic clove, chopped </td>
</tr>
<tr>
<td>1 cup chicken livers </td>
</tr>
<tr>
<td>1 tablespoon bourbon </td>
</tr>
<tr>
<td>Thyme </td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>Freshly ground black pepper </td>
</tr>
<tr>
<td>Clarified butter, optional </td>
</tr>
</tbody></table>
<p>Melt 2 tablespoons of the butter in a large skillet. Add onion and garlic. Cook on
                low heat until soft, about 6 minutes. Increase heat to medium, add the livers, and
                sauté 2 to 3 minutes, until firm. Remove from heat and cool. Coarsely chop the liver
                mixture with a knife, and move the chopped livers to a sturdy blender or a food
                processor fitted with the metal blade; process until smooth. Add remaining 2
                tablespoons butter and beat into liver mixture. Stir in bourbon and thyme. Season to
                taste with salt and pepper, making certain the mixture is well flavored. Spoon into
                ramekins or dishes and smooth the top. </p>
<p>If not serving immediately, cover with optional melted clarified butter. Chill or
                freeze. </p>
<div class="sidebar">
<p class="title">
<b></b>
</p>
<h4><a id="d0e2763" xmlns:saxon="http://icl.com/saxon"></a>Clarifying Butter </h4>
<p>Melt the butter and chill. When cold, remove and discard the top foam; save the
                    middle clarified portion; remove and discard the bottom milk solids. Store
                    clarified butter in the refrigerator or freezer.</p>
</div>
<h2><a id="d0e2767" xmlns:saxon="http://icl.com/saxon"></a>Guacamole </h2>
<p>
<span class="emphasis">
<em>Makes 1-1/2 to 2 cups </em>
</span>
</p>
<p>Everyone who loves avocados loves guacamole—and, of course, it can be store-bought,
                even purchased frozen. But freshly made guacamole from a ripe avocado is a treat. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1 ripe avocado </td>
</tr>
<tr>
<td>1/4 cup chopped onion </td>
</tr>
<tr>
<td>1/2 cup finely chopped tomato </td>
</tr>
<tr>
<td>2 garlic cloves, very finely chopped </td>
</tr>
<tr>
<td>1 teaspoon chopped fresh cilantro leaves </td>
</tr>
<tr>
<td>2 teaspoons fresh lime, lemon, or orange juice </td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>Freshly ground black pepper </td>
</tr>
<tr>
<td>1 fresh jalapeño pepper or other small hot pepper, seeded and finely
                        chopped, optional </td>
</tr>
<tr>
<td>Hot sauce, optional </td>
</tr>
</tbody></table>
<p>Cut the avocado in half, remove the seed, and scoop out the avocado flesh with a
                spoon or other gadget. Mash the flesh with the fork in a medium bowl. </p>
<p>Add the onion, tomato, garlic, cilantro, and citrus juice; mix well. Season to taste
                with salt and pepper. Stir in jalapeño and hot sauce if desired. </p>
<p>Cover tightly with plastic wrap and store in the refrigerator. Serve with <a class="link" href="#/ch04#brittlebread" title="Sour Cream Brittlebread">Sour
                    Cream Brittlebread</a> or tortilla chips. </p>
<div class="sidebar">
<p class="title">
<b></b>
</p>
<h4><a id="d0e2807" xmlns:saxon="http://icl.com/saxon"></a>Avocados </h4>
<p>Mature Hass avocados are still hard when picked, as they will never ripen on a
                    tree. They must ripen after picking, thus making them easier to ship.
                    (Interestingly, other varieties from the equator do ripen on the tree.) To speed
                    ripening, move the avocados to a brown paper bag for 2 to 3 days. Do not
                    refrigerate. </p>
<p>Avocados turn brown when peeled. A little acid such as lemon juice will prevent
                    browning, but always cover tightly with plastic wrap. Alas, leaving the pit in,
                    as lore suggests, beguiles but doesn’t work!</p>
</div>
<div class="sidebar">
<p class="title">
<b></b>
</p>
<h4><a id="d0e2814" xmlns:saxon="http://icl.com/saxon"></a>Charleston Cilantro </h4>
<p>“Charleston cilantro” is a commonly used name for the herb also known as
                    “Vietnamese cilantro.” It has a rounded leaf that is quite different from the
                    leaf of a cilantro plant, but the taste is similar. Regular cilantro does not
                    like the summer heat, so I frequently use this.</p>
</div>
<h2><a id="d0e2818" xmlns:saxon="http://icl.com/saxon"></a>Fresh Trout Dip </h2>
<p>
<span class="emphasis">
<em>Serves 6 to 8 </em>
</span>
</p>
<p>Southerners relish seafood dips, from shrimp paste to blue crab dip, and they have a
                long history in this part of the country. When we returned from the market with some
                beautiful fresh rainbow trout, it was harder than originally anticipated to find a
                dip recipe with fresh trout. Every recipe was for smoked trout. So we made up this
                recipe and served it in green onion and Gruyère cheese puffs—oh-so good! The clean,
                sweet taste of fresh trout in this recipe really shines. Served with crackers or on
                slices of cucumbers, it makes a nice light starter, or pipe into <span class="link" href="#/ch13#puffs" title="Link target outside of Sample">Nathalie’s Cream
                Puffs</span>. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>2 fresh trout fillets with skin on, deboned </td>
</tr>
<tr>
<td>1/2 cup white wine </td>
</tr>
<tr>
<td>2 teaspoons chopped fresh thyme </td>
</tr>
<tr>
<td>2 tablespoons cream cheese </td>
</tr>
<tr>
<td>2 tablespoons mayonnaise </td>
</tr>
<tr>
<td>1/2 teaspoon Worcestershire sauce </td>
</tr>
<tr>
<td>1/4 teaspoon hot sauce </td>
</tr>
<tr>
<td>1 teaspoon salt </td>
</tr>
<tr>
<td>Freshly ground black pepper </td>
</tr>
<tr>
<td>1 shallot, minced </td>
</tr>
<tr>
<td>Grated rind of 1 orange, no white attached </td>
</tr>
</tbody></table>
<p>Rinse the fillets and pat dry. Cut each fillet into 3 or 4 pieces and move to a
                medium saucepan. </p>
<p>Add wine mixed with enough water to cover fish. Bring the liquid to just below a
                simmer and poach the fish uncovered over low heat, keeping temperature just below a
                simmer, for 4 to 6 minutes, or until skin peels off easily. Remove and set on a rack
                over a plate to catch the dripping and allow to cool. </p>
<p>For the dip, whisk together thyme, cheese, mayonnaise, Worcestershire sauce, and hot
                sauce until smooth. Season to taste with salt and pepper. Add shallot and orange
                rind. </p>
<p>Break fish into small flakes with fingers, discarding any small bones. Fold into the
                sauce mixture. Taste for seasoning, adding extra salt or hot sauce as necessary.
                Serve chilled. Can be made a day ahead and refrigerated. </p>
<p><span class="bold"><strong>Variation:</strong></span> Substitute smoked trout for the
                poached trout.</p>
<h2><a id="d0e2865" xmlns:saxon="http://icl.com/saxon"></a>Marion’s Crab Spread </h2>
<p>
<span class="emphasis">
<em>Makes 2 pounds </em>
</span>
</p>
<p>Marion Sullivan has been a foodie for the many years I’ve known her. She has worked
                with me on half a dozen books, co-authored <span class="emphasis"><em>Nathalie
                        Dupree’s Shrimp and Grits,</em></span> and is an excellent food writer and
                editor for <span class="emphasis"><em>Charleston Magazine.</em></span> Before that,
                she catered. True to her style, this spread makes an elegant starter and stars on a
                buffet table. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1 pound cream cheese, room temperature </td>
</tr>
<tr>
<td>1 celery rib, finely chopped </td>
</tr>
<tr>
<td>2 shallots, finely chopped </td>
</tr>
<tr>
<td>2 tablespoons fresh lemon juice </td>
</tr>
<tr>
<td>2 tablespoons Worcestershire sauce </td>
</tr>
<tr>
<td>2 tablespoons horseradish </td>
</tr>
<tr>
<td>1 cup tomato sauce or ketchup </td>
</tr>
<tr>
<td>1 pound fresh lump crabmeat, picked over and cleaned, with all shell
                        fragments removed </td>
</tr>
</tbody></table>
<p>Whip cream cheese with an electric hand mixer until fluffy. Fold in celery and
                shallots, and spread mixture onto a serving platter, making the shape of a pie with
                a slight ridge around the edge. </p>
<p>Mix lemon juice, Worcestershire sauce, and horseradish. Add tomato sauce or ketchup.
                Spread onto cream cheese. Top with crabmeat before serving. </p>
<p><span class="bold"><strong>Variation:</strong></span> Substitute 1/4 cup finely
                chopped fresh fennel for the celery.</p>
<h2><a id="d0e2905" xmlns:saxon="http://icl.com/saxon"></a>Cold Shrimp Paste Spread </h2>
<p>
<span class="emphasis">
<em>Makes 2 cups </em>
</span>
</p>
<p>There are nearly half a dozen recipes for shrimp paste, most falling into two types:
                a puréed cold spread of cooked shrimp or a baked loaf of mousse-like shrimp paste
                that is sliced and served hot. The ingredients vary little—only the technique—and
                are found in most coastal Southern cookbooks like <span class="emphasis"><em>Charleston Receipts</em></span> and <span class="emphasis"><em>Two
                        Hundred Years of Charleston Cooking.</em></span> Variations include celery
                salt, Worcestershire sauce, mayonnaise, and sherry. This paste is frequently used in
                shrimp-paste sandwiches, open-faced or closed. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1-1/2 pounds large shrimp, <span class="link" href="#/ch09#cookshrimp" title="Link target outside of Sample">cooked and peeled</span>
</td>
</tr>
<tr>
<td>1-1/2 cups butter, room temperature </td>
</tr>
<tr>
<td>3/4 teaspoon salt </td>
</tr>
<tr>
<td>Freshly ground white or black pepper </td>
</tr>
<tr>
<td>1/2 teaspoon ground hot red pepper </td>
</tr>
<tr>
<td>1/4 teaspoon freshly grated nutmeg </td>
</tr>
<tr>
<td>Lettuce leaves or other greens </td>
</tr>
</tbody></table>
<p>Process the shrimp in a food processor or blender until puréed and paste-like. Remove
                to a separate bowl. </p>
<p>Beat the butter until soft and white. Stir the butter into the shrimp, season with
                salt, white or black pepper, hot red pepper, and nutmeg; stir until smooth. </p>
<p>Cover and refrigerate. When ready to serve, surround with toast points or crackers,
                or wrap in lettuce leaves or collard greens. </p>
<h2><a id="d0e2944" xmlns:saxon="http://icl.com/saxon"></a>Hot or Cold Shrimp Paste </h2>
<p>
<span class="emphasis">
<em>Makes 3 cups </em>
</span>
</p>
<p>This is the other of the two famous shrimp “pastes,” but it’s technically a terrine.
                My fondest memories are of it being served by a group of Charleston, South Carolina,
                men who gather monthly to hear scholarly lectures and discussions. Once a year,
                ladies are invited and the men prepare a great feast including shrimp paste. After
                the repast, the group gathers at the foot of the mansion’s steps, next to the
                Battery, and proceeds to march around downtown Charleston holding flaming torches,
                with hired police to escort them. It is great fun to see the tourists agog at such
                shenanigans and know there is no meaning to the march besides just fun. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1-1/4 pounds large shrimp, <span class="link" href="#/ch09#cookshrimp" title="Link target outside of Sample">cooked and peeled</span>
</td>
</tr>
<tr>
<td>1 cup butter </td>
</tr>
<tr>
<td>Dash of nutmeg </td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>Freshly ground black pepper </td>
</tr>
</tbody></table>
<p>Preheat oven to 350 degrees. </p>
<p>Process the shrimp in a food processor or blender until puréed and paste-like. Remove
                to a separate bowl. </p>
<p>Beat the butter with an electric hand mixer until soft and white. Stir the butter
                into the shrimp, season with nutmeg, and salt and pepper to taste, and stir until
                smooth. </p>
<p>Instead of chilling, move to a buttered loaf pan and bake until the paste comes away
                from the sides and is firm to the touch. Go around the sides with a knife, cover
                with a plate, and invert. Tap the pan gently if needed. </p>
<p>Slice and serve hot, or chill overnight and serve cold. Accompany with crackers or
                toast, or wrapped in lettuce leaves or other greens.</p>
<div class="sidebar">
<p class="title">
<b></b>
</p>
<h4><a id="d0e2978" xmlns:saxon="http://icl.com/saxon"></a>Grinding Shrimp </h4>
<p>The shrimp in these pastes, or pâtés, are very finely ground. In France, they
                    would be rubbed through a fine sieve. The cooked shrimp can also be put through
                    a meat grinder twice, cleaning out the grinder with a biscuit in between
                    grindings, or pounded with a mortar and pestle until finely ground. Food
                    processors now make short work of this once arduous task. </p>
<p><span class="bold"><strong>Variation:</strong></span> Add a bit of chopped fresh
                    lemon thyme or fresh basil in the summer.</p>
</div>
<h2><a id="pimento" xmlns:saxon="http://icl.com/saxon"></a>Pimento Cheese </h2>
<p>
<span class="emphasis">
<em>Makes 3 cups </em>
</span>
</p>
<p>As every Southerner knows, this is pronounced “pimentacheese,” as if it were one
                word. Every pimentacheese recipe is different—some chunky, some smooth. Most favor
                their mother’s or grandmother’s recipe. My husband prefers it made with Duke’s
                mayonnaise. It keeps at least a week in the refrigerator. Process the mixture longer
                for a smooth spread that can be used as a dip or piped onto crackers or celery
                sticks. It’s not bad slathered between slices of fried green tomatoes either. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>12 ounces grated Cheddar cheese </td>
</tr>
<tr>
<td>2 (4-ounce) jars pimento, drained, or <span class="link" href="#/ch07b#roastedpeppers" title="Link target outside of Sample">Charred Red
                            Peppers</span></td>
</tr>
<tr>
<td>1 cup mayonnaise </td>
</tr>
<tr>
<td>Salt </td>
</tr>
<tr>
<td>Freshly ground black pepper </td>
</tr>
</tbody></table>
<p>Pulse cheese, pimento, and mayonnaise in a food processor fitted with the metal
                blade. Process until slightly chunky but spreadable. Season to taste with salt and
                pepper.</p>
<p><span class="bold"><strong>Variation:</strong></span> Use half sharp Cheddar and half
                white Cheddar, grated. </p>
<p>
<span class="bold">
<strong>Variation: Lulu Paste</strong>
</span>
</p>
<p>To make Richmond’s Lulu Paste, add 1 small chopped onion, 1/2 cup ketchup, and a
                pinch of dry mustard in the food processor with the cheese, pimento, and mayonnaise. </p>
<p>Make a BLT using pimento cheese instead of mayonnaise. </p>
<h2><a id="d0e3023" xmlns:saxon="http://icl.com/saxon"></a>Marinated Olives </h2>
<p>
<span class="emphasis">
<em>Makes 2 cups </em>
</span>
</p>
<p>When the tree in my front yard is brimming with the tiniest of olives, I think of the
                ways I can flavor them. Of course, having one’s own tree is not necessary, as this
                works with any cured olive. There are hundreds of variations. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>2 cups brine-cured unpitted olives, such as kalamata </td>
</tr>
<tr>
<td>1/3 cup olive oil </td>
</tr>
<tr>
<td>1 bay leaf </td>
</tr>
<tr>
<td>4 sprigs fresh rosemary </td>
</tr>
<tr>
<td>4 garlic cloves, sliced </td>
</tr>
<tr>
<td>1 teaspoon grated lemon rind, no white attached </td>
</tr>
</tbody></table>
<p>Heat the oil, bay leaf, rosemary sprigs, and garlic over very low heat in a small
                saucepan until just warmed through. Set aside to cool. </p>
<p>Move the olives to a serving bowl, plastic ziplock bag, or other container. Pour
                cooled oil mixture over olives. Sprinkle with lemon rind. </p>
<p>Refrigerate olives until ready to serve, at least 12 hours for best flavor, 2 or 3
                days for better flavor, and store refrigerated up to several weeks. </p>
<div class="mediaobject" title="Photo of the recipe or step above.">
<img alt="Photo of the recipe or step above." src="images/RCM_1648.jpg"/>
</div>
<h2><a id="butteredpecans" xmlns:saxon="http://icl.com/saxon"></a>Buttered Pecans </h2>
<p>
<span class="emphasis">
<em>Makes 4 cups </em>
</span>
</p>
<p>Buttered pecan halves make a visitor feel welcome. Salt can make pecans weepy over a
                period of time, so store them unsalted and then salt them shortly before serving. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>4 cups shelled pecan halves, about 1 pound </td>
</tr>
<tr>
<td>1 cup salted butter, melted </td>
</tr>
<tr>
<td>Salt </td>
</tr>
</tbody></table>
<p>Preheat oven to 250 degrees. Line 2 rimmed baking sheets with aluminum foil or
                parchment paper. </p>
<p>Toss the pecans in a bowl with the butter. Spread pecans on the prepared baking
                sheets. Bake for 1 hour, stirring occasionally, until the pecans are a nice brown
                but not burned. Remove from the oven. </p>
<p>If serving right away, add salt to taste. Otherwise, cool and store tightly covered.
                Serve as is, or reheat briefly and add salt while hot. </p>
<p>
<span class="bold">
<strong>Variation:</strong>
</span>
<span class="bold">
<strong>Spicy Toasted Pecans</strong>
</span>
</p>
<p>Stir together 2 teaspoons paprika, 1 teaspoon onion powder, 1/2 teaspoon hot red
                pepper, 2 teaspoons ground cumin, and 2 teaspoons granulated sugar. Sprinkle this
                seasoning mix over the buttered pecans before spreading out on prepared baking
                sheets. Bake at 300 degrees for 25 minutes. </p>
<h2><a id="d0e3088" xmlns:saxon="http://icl.com/saxon"></a>Louis Limestone’s Roasted
                Peanuts </h2>
<p>
<span class="emphasis">
<em>Quantity as desired </em>
</span>
</p>
<p>Roasted peanuts have a place of their own in Southern hearts and on Southern tables.
                They stand on their own at ball games and in bars, and are added to everything from
                sweets to . . . nuts. Roasting in the shell gives a more intense flavor. </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>Fresh peanuts, in shell </td>
</tr>
</tbody></table>
<p>Preheat oven to 350 degrees. </p>
<p>Spread peanuts in a single layer on a rimmed baking sheet. Bake 30 minutes, turning
                frequently. Serve hot, warm, or at room temperature. If storing, remove from shell
                and keep airtight.</p>
<div class="sidebar">
<p class="title">
<b></b>
</p>
<p>The peanut had a circuitous route from South America to Africa, then arriving
                    here with the early slave ships to feed the slaves on board. Originally only
                    slaves planted and ate peanuts. They were dismissed by others, who fed them to
                    animals such as the pig. It was during the Civil War that they became revered by
                    Southerners dependent on them as a valuable source of nutrition. Southern
                    soldiers wrote songs about peanuts, and Union soldiers were introduced to them
                    as well. </p>
</div>
<h2><a id="d0e3107" xmlns:saxon="http://icl.com/saxon"></a>Julius’s Peanuts with Hot
                Peppers </h2>
<p>
<span class="emphasis">
<em>Makes 1/2 pound </em>
</span>
</p>
<p>Julius Waring Walker was an ambassador to Liberia and Upper Volta, and lived in other
                African countries as well. He sent me this recipe, which is served there. I use
                canned roasted peanuts. Although they “last forever,” Julius says, they won’t last
                long on the party table. Very tasty indeed! </p>
<table border="0" class="simplelist" summary="Simple list">
<tbody><tr>
<td>1/2 pound dry roasted peanuts </td>
</tr>
<tr>
<td>2 to 3 dried hot peppers, seeded and finely chopped </td>
</tr>
<tr>
<td>1 teaspoon salt, optional </td>
</tr>
</tbody></table>
<p>Toss the peanuts and peppers together. Move to glass jars and cover tightly. Add salt
                as needed before serving. </p>
<div class="sidebar">
<p class="title">
<b></b>
</p>
<h4><a id="d0e3126" xmlns:saxon="http://icl.com/saxon"></a>Boiled Peanuts </h4>
<p>These are a treat unknown in many parts of the country where perhaps pretzels
                    reign. But my memories of fraternity parties include mainly boiled peanuts and
                    beer. The volume of peanut shells left on the floor was a matter of honor—as in,
                    “We had five loads of beer cans and fifteen bags of peanut shells to take to the
                    dump.” </p>
<p>Although I enjoy the occasional boiled peanut, I don’t dream about eating them. I
                    think mostly men dream about them. My husband will insist on stopping any time
                    he passes the boiled peanut stand by the side of the road, particularly if an
                    old iron wash pot is atop a fire. </p>
<p>Wash freshly harvested (green) peanuts in a mild solution of kitchen detergent.
                    Rinse in clear water. Cover peanuts with a medium brine (10 ounces of salt to 1
                    gallon of water) in a large saucepan. Bring to the boil, reduce heat, and simmer
                    for 45 minutes or up to 2 hours, testing occasionally, until kernels are tender.
                    (The time may vary greatly.) Taste for saltiness. Rinse immediately or allow to
                    sit in brine an additional 15 minutes. Pour off water and drain before serving.
                    Peanuts are ready for shelling and eating immediately; however, they can be
                    refrigerated for up to 5 days or frozen and reheated in the microwave. They are
                    best piping hot. If using older peanuts, the cooking time will be longer. </p>
<div class="mediaobject" title="Photo of the recipe or step above.">
<img alt="Photo of the recipe or step above." src="images/_IIP5514.jpg"/>
</div>
</div>
</div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// ch04.vue?d3085288